import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toPng } from 'html-to-image';
import { toast } from 'sonner';
import { X, Search, Loader2, TrendingUp, TrendingDown, History, Download, Share2, ClipboardCopy, GitCompare, Star, ExternalLink, Pin, PinOff, StickyNote, Check } from 'lucide-react';
import HoverBar from './HoverBar';
import useTipClamp from './useTipClamp';
import RepinPriceButton from './RepinPriceButton';
import MmfCompareCard from './MmfCompareCard';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// Slot colors — A = blue/sky, B = violet. Keep these tokens in one place so
// headers, borders, badges, and chart bars/lines stay perfectly consistent.
// ---------------------------------------------------------------------------
const SLOT_STYLES = {
  A: {
    badgeBg: 'bg-sky-500',
    borderLocked: 'border-sky-500',
    ringFocus: 'focus-within:ring-sky-400',
    textLocked: 'text-sky-600 dark:text-sky-400',
    barFill: '#0ea5e9', // sky-500
    lineStroke: '#0ea5e9',
  },
  B: {
    badgeBg: 'bg-violet-500',
    borderLocked: 'border-violet-500',
    ringFocus: 'focus-within:ring-violet-400',
    textLocked: 'text-violet-600 dark:text-violet-400',
    barFill: '#a78bfa', // violet-400
    lineStroke: '#a78bfa',
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Classify the stock's dividend profile into a short signal badge. */
function computeSignal(detail, growth) {
  if (!detail) return null;
  // Round 96b (Feb 2026) — Non-Dividend Expansion. A non-div ticker
  // (TSLA, AMZN, DIBS, etc.) should NOT be labeled "Flat Dividend" —
  // that label implies a stale dividend stream. Short-circuit to a
  // dedicated "Non-Dividend" pill which surfaces the quality tier
  // when one has been scored. Falls back to the generic label when
  // unscored.
  if (detail.pays_dividend === false) {
    const tier = detail.non_dividend_quality_tier;
    const isGrowthTier = tier === 'GROWTH-S' || tier === 'GROWTH-A';
    return {
      label: tier && tier !== 'SPECULATIVE'
        ? `Non-Dividend · ${tier.replace('GROWTH-', 'G-')}`
        : tier === 'SPECULATIVE'
        ? 'Non-Dividend · Speculative'
        : 'Non-Dividend',
      tone: isGrowthTier ? 'emerald' : tier === 'SPECULATIVE' ? 'amber' : 'sky',
      Icon: isGrowthTier ? TrendingUp : TrendingDown,
    };
  }
  const yld = detail.dividend_yield || 0;
  const payout = detail.payout_ratio || 0;
  const growth5 = growth?.growth_5yr ?? null;

  // Healthy growth — dividend compounding up year over year at a safe payout.
  if (growth5 !== null && growth5 >= 8 && (!payout || payout < 95)) {
    return { label: 'Healthy Growth', tone: 'emerald', Icon: TrendingUp };
  }
  // Volatile / high yield — juicy yield but either unsustainable or no growth.
  if (yld >= 12 || (growth5 !== null && growth5 < -3)) {
    return { label: 'Volatile Income', tone: 'amber', Icon: TrendingDown };
  }
  if (growth5 !== null && growth5 > 0) {
    return { label: 'Steady Income', tone: 'sky', Icon: TrendingUp };
  }
  return { label: 'Flat Dividend', tone: 'slate', Icon: TrendingDown };
}

function SignalPill({ signal }) {
  if (!signal) return <span className="text-muted-foreground">—</span>;
  const toneMap = {
    emerald: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30',
    amber: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30',
    sky: 'bg-sky-500/10 text-sky-600 dark:text-sky-400 border-sky-500/30',
    slate: 'bg-muted text-muted-foreground border-border',
  };
  const Icon = signal.Icon;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold ${toneMap[signal.tone]}`}
      data-testid={`signal-pill-${signal.label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <Icon className="w-3 h-3" />
      {signal.label}
    </span>
  );
}

function fmtPct(v, withSign = false) {
  if (v === null || v === undefined || v === '' || Number.isNaN(v)) return null;
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (Number.isNaN(n)) return null;
  const s = withSign && n > 0 ? '+' : '';
  return `${s}${n.toFixed(2)}%`;
}

function fmtUsd(v) {
  if (v === null || v === undefined || Number.isNaN(v)) return null;
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (Number.isNaN(n)) return null;
  return `$${n.toFixed(2)}`;
}

/** Winner comparator: returns 'a', 'b', or null when tied / missing data. */
function winner(a, b, direction = 'higher') {
  if (a === null || a === undefined || b === null || b === undefined) return null;
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  if (a === b) return null;
  if (direction === 'higher') return a > b ? 'a' : 'b';
  return a < b ? 'a' : 'b';
}

// ---------------------------------------------------------------------------
// Search box — shows live ticker + yield suggestions. Empty until user types.
// ---------------------------------------------------------------------------
function SearchBox({ slot, onLock, mmfMode }) {
  const styles = SLOT_STYLES[slot];
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const debounceRef = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    // P158·15 (Feb 2026) — When locked to MMF-only (partner slot is
    // a Money Market Fund), an empty query still fetches — the search
    // doubles as a scrollable dropdown of the 65 MMF tickers so users
    // can browse without typing. In all other modes, empty = no fetch.
    if (!q.trim() && mmfMode !== 'only') {
      setResults([]);
      return;
    }
    setBusy(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        // Round 96b (Feb 2026) — Pass `dividend_filter: 'all'` so the
        // compare search surfaces non-dividend tickers (TSLA, AMZN,
        // PLTR, etc.) too.
        // P158·15 — Forward `is_money_market_fund` so:
        //   `only` = partner is an MMF → results locked to the 65 XX tickers
        //   `hide` = partner is a regular stock → MMFs excluded (they'd
        //            compare to a lot of blanks and break the panel).
        const params = {
          q: q.trim(),
          limit: mmfMode === 'only' ? 200 : 8,
          dividend_filter: 'all',
        };
        if (mmfMode) params.is_money_market_fund = mmfMode;
        const r = await axios.get(`${API}/stocks/search`, {
          params,
          withCredentials: true,
        });
        setResults(Array.isArray(r.data) ? r.data : []);
      } catch {
        setResults([]);
      } finally {
        setBusy(false);
      }
    }, 200);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q, mmfMode]);

  useEffect(() => {
    function onDocClick(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  return (
    <div
      ref={boxRef}
      className={`relative w-full rounded-xl border-2 border-dashed border-border bg-muted/30 px-3 py-2.5 transition-colors focus-within:ring-2 ${styles.ringFocus}`}
      data-testid={`compare-search-${slot}`}
    >
      <div className="flex items-center gap-2">
        <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${styles.badgeBg} text-xs font-bold text-white`}>
          {slot}
        </span>
        <Search className="w-4 h-4 text-muted-foreground shrink-0" />
        <input
          type="text"
          value={q}
          placeholder={mmfMode === 'only' ? `Pick an MMF ${slot}…` : `Ticker ${slot}…`}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          className="flex-1 min-w-0 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          data-testid={`compare-search-input-${slot}`}
        />
        {busy && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
      </div>
      {mmfMode === 'only' && (
        <div className="mt-1 text-[10px] font-semibold uppercase tracking-wide text-cyan-700 dark:text-cyan-300" data-testid={`compare-mmf-lock-${slot}`}>
          Locked to Money Market Funds — pick any of the 65 XX-tickers
        </div>
      )}

      {open && (q.trim().length > 0 || mmfMode === 'only') && (
        <div
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-72 overflow-y-auto rounded-lg border bg-popover shadow-lg"
          data-testid={`compare-search-results-${slot}`}
        >
          {results.length === 0 && !busy && (
            <div className="px-3 py-3 text-xs text-muted-foreground">No matches.</div>
          )}
          {results.map((s) => (
            <button
              key={s.ticker}
              type="button"
              onClick={() => {
                setOpen(false);
                setQ('');
                onLock(s.ticker);
              }}
              className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-muted transition-colors border-b border-border/40 last:border-b-0"
              data-testid={`compare-search-result-${slot}-${s.ticker}`}
            >
              <div className="flex flex-col min-w-0">
                <span className="text-sm font-bold font-mono">{s.ticker}</span>
                <span className="truncate text-[11px] text-muted-foreground max-w-[140px] sm:max-w-[170px]">{s.name}</span>
              </div>
              <span className="shrink-0 text-sm font-bold font-mono text-emerald-600 dark:text-emerald-400">
                {s.dividend_yield ? `${s.dividend_yield.toFixed(2)}%` : '—'}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Locked card — ticker + name + X to unlock. Adds:
//   - Star bookmark on the LEFT of the ticker (toggles watchlist,
//     fires sonner toast, no-op + login prompt if logged out).
//   - Clickable ticker that closes the Compare modal and opens the
//     full Stock modal for that ticker. Hover tooltip "Click to view
//     full stock data" — implemented as a `title` attr (works on mouse)
//     plus a small visible inline hint that appears on hover only on
//     desktop (sm+) so it doesn't crowd phone view.
// ---------------------------------------------------------------------------
function LockedCard({ slot, ticker, name, loading, onUnlock, watchlist, onToggleWatchlist, user, onLoginRequired, onOpenStockModal }) {
  const styles = SLOT_STYLES[slot];
  const inWatch = Array.isArray(watchlist) && watchlist.includes(ticker);

  const handleStarClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!user) {
      if (typeof onLoginRequired === 'function') onLoginRequired();
      return;
    }
    if (typeof onToggleWatchlist === 'function') onToggleWatchlist(ticker);
    if (inWatch) {
      toast(`★ ${ticker} removed from watchlist`, { duration: 2200 });
    } else {
      toast.success(`★ ${ticker} added to watchlist`, {
        duration: 2800,
        description: 'View it from the header → Watchlist',
      });
    }
  };

  const handleTickerClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (typeof onOpenStockModal === 'function') onOpenStockModal(ticker);
  };

  return (
    <div
      className={`relative w-full rounded-xl border-2 ${styles.borderLocked} bg-background px-3 py-2.5`}
      data-testid={`compare-locked-${slot}`}
    >
      <div className="flex items-center gap-2">
        <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${styles.badgeBg} text-xs font-bold text-white`}>
          {slot}
        </span>
        {/* Star bookmark — to the LEFT of the ticker */}
        <button
          type="button"
          onClick={handleStarClick}
          className="shrink-0 p-0.5 rounded hover:bg-primary/10 transition-colors"
          aria-label={inWatch ? `Remove ${ticker} from watchlist` : `Add ${ticker} to watchlist`}
          title={inWatch ? `Remove ${ticker} from watchlist` : `Add ${ticker} to watchlist`}
          data-testid={`compare-star-${slot}`}
        >
          <Star
            className={`w-4 h-4 ${
              inWatch ? 'fill-primary text-primary' : 'text-muted-foreground hover:text-primary'
            }`}
          />
        </button>
        <div className="flex-1 min-w-0">
          {/* Ticker — clickable, opens Stock modal. The inline "Click
              to view full stock data" hint only shows on hover at sm+
              so phone view stays uncluttered. */}
          <div className="group/tk flex items-center gap-1.5 relative">
            <button
              type="button"
              onClick={handleTickerClick}
              className={`font-bold font-mono text-sm ${styles.textLocked} hover:underline underline-offset-2 inline-flex items-center gap-1`}
              title="Click to view full stock data"
              data-testid={`compare-ticker-${slot}`}
            >
              {ticker}
              <ExternalLink className="w-3 h-3 opacity-50 group-hover/tk:opacity-100 transition-opacity" />
            </button>
            {loading && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
            {/* Desktop-only floating hint that fades in on hover. The
                hint sits BELOW the ticker so it never gets clipped on
                a narrow card; on phone we rely on the title tooltip
                + the underline + arrow icon as the affordance. */}
            <span
              className="hidden sm:block pointer-events-none absolute left-0 top-full mt-0.5 whitespace-nowrap text-[9px] font-semibold uppercase tracking-wider text-muted-foreground bg-popover border border-border rounded px-1.5 py-0.5 shadow-sm opacity-0 group-hover/tk:opacity-100 transition-opacity z-10"
              data-testid={`compare-ticker-hint-${slot}`}
            >
              Click to view full stock data
            </span>
          </div>
          <div className="truncate text-[11px] text-muted-foreground">{name || '—'}</div>
        </div>
        <button
          type="button"
          onClick={onUnlock}
          className="shrink-0 rounded-full p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          aria-label={`Clear ${slot}`}
          data-testid={`compare-unlock-${slot}`}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Comparison row
// ---------------------------------------------------------------------------
function Row({ label, a, b, winnerSide, strong = false }) {
  const greenA = winnerSide === 'a' ? 'text-emerald-600 dark:text-emerald-400 font-bold' : '';
  const greenB = winnerSide === 'b' ? 'text-emerald-600 dark:text-emerald-400 font-bold' : '';
  return (
    <div
      className="grid grid-cols-[1fr,1fr,1fr] items-center gap-2 border-b border-border/40 px-2 py-2.5 sm:px-4 last:border-b-0"
      data-testid={`compare-row-${label.toLowerCase().replace(/\s|\./g, '-')}`}
    >
      <div className={`text-xs sm:text-sm ${strong ? 'font-semibold' : 'text-muted-foreground'} text-left`}>
        {label}
      </div>
      <div className={`text-sm sm:text-base text-center font-mono ${greenA}`}>
        {a === null || a === undefined ? <span className="text-muted-foreground">—</span> : a}
      </div>
      <div className={`text-sm sm:text-base text-center font-mono ${greenB}`}>
        {b === null || b === undefined ? <span className="text-muted-foreground">—</span> : b}
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div className="px-2 pt-4 pb-1 text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground sm:px-4">
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Merged bar chart — annual dividends per share for A & B side-by-side.
// Pure CSS/flex (no recharts) to match the rest of the app.
// ---------------------------------------------------------------------------
function MergedBarChart({ a, b, aTicker, bTicker }) {
  const aData = a?.annual_data || [];
  const bData = b?.annual_data || [];
  const years = Array.from(new Set([...aData.map((d) => d.year), ...bData.map((d) => d.year)])).sort();
  if (years.length === 0) return null;

  const lookupA = Object.fromEntries(aData.map((d) => [d.year, d.amount]));
  const lookupB = Object.fromEntries(bData.map((d) => [d.year, d.amount]));
  const maxAmount = Math.max(
    ...years.map((y) => Math.max(lookupA[y] || 0, lookupB[y] || 0)),
    0.01,
  );
  const rawCeiling = maxAmount * 1.2;
  let ceiling;
  if (rawCeiling <= 0.5) ceiling = 0.5;
  else if (rawCeiling <= 1) ceiling = 1;
  else if (rawCeiling <= 2) ceiling = 2;
  else if (rawCeiling <= 5) ceiling = 5;
  else ceiling = Math.ceil(rawCeiling / 2) * 2;
  const yLabels = [];
  for (let i = 4; i >= 0; i--) {
    const v = (ceiling * i) / 4;
    yLabels.push(`$${v % 1 === 0 ? v : v.toFixed(2)}`);
  }

  return (
    <div className="rounded-lg border bg-card p-3 sm:p-4" data-testid="compare-bar-chart">
      <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground mb-3">
        Dividend Growth History
      </h3>
      <div className="flex items-end">
        <div className="flex h-56 flex-col justify-between pr-2 shrink-0">
          {yLabels.map((l, i) => (
            <span key={i} className="text-[9px] font-mono text-muted-foreground leading-none">{l}</span>
          ))}
        </div>
        <div className="flex-1 flex items-end gap-3 sm:gap-6 h-56 border-b border-l border-border/40 pl-2 pr-1">
          {years.map((y, idx) => {
            const aAmt = lookupA[y] || 0;
            const bAmt = lookupB[y] || 0;
            const aPct = (aAmt / ceiling) * 100;
            const bPct = (bAmt / ceiling) * 100;

            // Year-over-year growth labels — only when we actually have
            // the prior year's amount > 0 for that ticker. Otherwise fall
            // back to the plain "TICKER YEAR: $X" string (no "NaN"/"—").
            // Returns a React node so we can color the ▲/▼ arrow.
            const buildLabel = (ticker, amt, lookup) => {
              const base = `${ticker} ${y}: $${amt.toFixed(2)}`;
              if (idx > 0) {
                const prevY = years[idx - 1];
                const prevAmt = lookup[prevY];
                if (typeof prevAmt === 'number' && prevAmt > 0) {
                  const pct = ((amt - prevAmt) / prevAmt) * 100;
                  const up = pct >= 0;
                  const arrow = up ? '▲' : '▼';
                  const sign = up ? '+' : '';
                  const color = up ? 'text-emerald-400' : 'text-rose-400';
                  return (
                    <>
                      {base}{' '}
                      <span className={color}>
                        ({arrow} {sign}{pct.toFixed(1)}% vs {prevY})
                      </span>
                    </>
                  );
                }
              }
              return base;
            };

            return (
              <div key={y} className="flex-1 flex flex-col items-center justify-end h-full min-w-0">
                <div className="flex items-end gap-1 sm:gap-1.5 h-full w-full justify-center">
                  {aData.length > 0 && (
                    <HoverBar
                      label={buildLabel(aTicker, aAmt, lookupA)}
                      className="flex-1 max-w-[28px] sm:max-w-[44px] rounded-t-sm transition-all"
                      style={{ height: `${Math.max(aPct, 2)}%`, backgroundColor: SLOT_STYLES.A.barFill }}
                    />
                  )}
                  {bData.length > 0 && (
                    <HoverBar
                      label={buildLabel(bTicker, bAmt, lookupB)}
                      className="flex-1 max-w-[28px] sm:max-w-[44px] rounded-t-sm transition-all"
                      style={{ height: `${Math.max(bPct, 2)}%`, backgroundColor: SLOT_STYLES.B.barFill }}
                    />
                  )}
                </div>
                <span className="mt-1 text-[10px] font-mono text-muted-foreground">{y}</span>
              </div>
            );
          })}
        </div>
      </div>
      {/* legend */}
      <div className="mt-3 flex items-center gap-4 text-[11px]">
        {aTicker && (
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: SLOT_STYLES.A.barFill }} />
            <span className="font-mono font-semibold">{aTicker} Div</span>
          </div>
        )}
        {bTicker && (
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: SLOT_STYLES.B.barFill }} />
            <span className="font-mono font-semibold">{bTicker} Div</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Merged yield history line chart (SVG path). Draws A + B overlaid.
// ---------------------------------------------------------------------------
function MergedYieldChart({ a, b, aTicker, bTicker, isDark }) {
  const svgRef = useRef(null);
  const tipRef = useRef(null);
  const [hoverIdx, setHoverIdx] = useState(null);
  // Round 89d — optional log-scale Y axis. Off by default. Only the
  // legend's "Log scale" toggle exposes it, and only when the linear
  // chart has a ≥3× skew between A & B peaks (otherwise it's noise).
  const [logScale, setLogScale] = useState(false);
  const tipShiftX = useTipClamp(tipRef, hoverIdx !== null, [hoverIdx]);

  const aMonthly = a?.monthly_data || [];
  const bMonthly = b?.monthly_data || [];
  if (aMonthly.length === 0 && bMonthly.length === 0) return null;

  const allY = [...aMonthly.map((d) => d.yield_pct || 0), ...bMonthly.map((d) => d.yield_pct || 0)].filter(
    (v) => v > 0,
  );
  // Linear bounds (always computed — needed for the scale legend even
  // when the chart renders in log mode).
  const yMaxLin = Math.max(...allY, 1) * 1.15;
  const yMinLin = 0;
  // Log bounds. Floor at 0.05% so we never log10(0). Anything in the
  // data below the floor is clamped to it (the chart still draws a
  // continuous line; the value is just visually pinned to the bottom).
  const LOG_FLOOR = 0.05;
  const minPositive = allY.length ? Math.min(...allY) : LOG_FLOOR;
  const yMinLog = Math.max(LOG_FLOOR, minPositive * 0.6);
  const yMaxLog = Math.max(yMaxLin, yMinLog * 10); // ensure at least 1 decade

  const useLog = logScale;
  const log10 = (v) => Math.log(Math.max(v, yMinLog)) / Math.LN10;
  const yMin = useLog ? yMinLog : yMinLin;
  const yMax = useLog ? yMaxLog : yMaxLin;

  const w = 600;
  const h = 160;
  const padL = 30;
  const padR = 10;
  const padT = 10;
  const padB = 25;

  const maxLen = Math.max(aMonthly.length, bMonthly.length);
  const xStep = (w - padL - padR) / Math.max(maxLen - 1, 1);

  // Centralised value→Y mapper used by paths, crossovers and the hover
  // tooltip dots. Branches on `useLog` so everything that draws the
  // chart sees the same transform.
  const valToY = (v) => {
    const raw = Math.max(v, useLog ? yMinLog : 0);
    if (useLog) {
      const num = log10(raw) - log10(yMinLog);
      const den = log10(yMaxLog) - log10(yMinLog);
      return padT + (h - padT - padB) * (1 - num / den);
    }
    return padT + (h - padT - padB) * (1 - (raw - yMinLin) / (yMaxLin - yMinLin));
  };

  const toPath = (arr) => {
    if (!arr || arr.length === 0) return null;
    return arr
      .map((d, i) => {
        const x = padL + i * xStep;
        const y = valToY(d.yield_pct || 0);
        return `${i === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
      })
      .join(' ');
  };

  const aPath = toPath(aMonthly);
  const bPath = toPath(bMonthly);

  // Detect yield crossovers between A and B. A crossover is any month where
  // the sign of (aYield - bYield) flips from the prior month. Only counted
  // when BOTH sides have a real yield_pct (> 0) — we skip missing data.
  // Produces the SVG coords for each crossover so we can render ⬥ markers.
  const crossovers = [];
  if (aMonthly.length > 0 && bMonthly.length > 0) {
    const aMap = Object.fromEntries(aMonthly.map((d) => [d.date, d]));
    const bMap = Object.fromEntries(bMonthly.map((d) => [d.date, d]));
    const shared = Object.keys(aMap)
      .filter((d) => bMap[d])
      .sort();
    let prevSign = null;
    shared.forEach((date, i) => {
      const aY = aMap[date].yield_pct || 0;
      const bY = bMap[date].yield_pct || 0;
      if (aY <= 0 || bY <= 0) return;
      const sign = aY - bY >= 0 ? 1 : -1;
      if (prevSign !== null && sign !== prevSign) {
        // Place the diamond at the midpoint between prev & current month on X,
        // and at the average yield on Y (approximates where lines actually cross).
        const idx = aMonthly.findIndex((d) => d.date === date);
        const prevIdx = idx - 1;
        const xMid = padL + ((prevIdx + idx) / 2) * xStep;
        const yMid = valToY((aY + bY) / 2);
        crossovers.push({ date, x: xMid, y: yMid, i });
      }
      prevSign = sign;
    });
  }

  // Y-axis labels. Linear: 5 evenly-spaced ticks. Log: 1-3-pattern ticks
  // (e.g., 0.1, 0.3, 1, 3, 10) so each gridline is a fixed log step and
  // the user can read the multiplicative scale at a glance.
  const yLabels = [];
  if (useLog) {
    const decadeLo = Math.floor(log10(yMinLog));
    const decadeHi = Math.ceil(log10(yMaxLog));
    const ticks = [];
    for (let d = decadeLo; d <= decadeHi; d += 1) {
      [1, 3].forEach((mult) => {
        const v = mult * 10 ** d;
        if (v >= yMinLog && v <= yMaxLog) ticks.push(v);
      });
    }
    // Top-down ordering so they line up with the grid lines below.
    ticks.sort((x, y) => y - x);
    ticks.forEach((v) => {
      const y = valToY(v);
      yLabels.push({ y, label: v >= 1 ? `${v.toFixed(0)}%` : `${v.toFixed(2)}%` });
    });
  } else {
    for (let i = 4; i >= 0; i -= 1) {
      const v = yMin + ((yMax - yMin) * i) / 4;
      const y = padT + ((h - padT - padB) * (4 - i)) / 4;
      yLabels.push({ y, label: `${v.toFixed(0)}%` });
    }
  }

  // X labels — first, middle, last
  const xPoints = aMonthly.length ? aMonthly : bMonthly;
  const xLabels =
    xPoints.length >= 3
      ? [
          { i: 0, label: xPoints[0].date?.slice(0, 7) },
          { i: Math.floor(xPoints.length / 2), label: xPoints[Math.floor(xPoints.length / 2)].date?.slice(0, 7) },
          { i: xPoints.length - 1, label: xPoints[xPoints.length - 1].date?.slice(0, 7) },
        ]
      : [];

  const gridColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
  const textColor = isDark ? '#9ca3af' : '#6b7280';

  // Map a mouse event's X coord (relative to the rendered SVG) to the nearest
  // data index. Uses getBoundingClientRect ratio so it works with the
  // non-uniform `preserveAspectRatio="none"` scaling this SVG uses.
  const handleMove = (e) => {
    const el = svgRef.current;
    if (!el || maxLen === 0) return;
    const rect = el.getBoundingClientRect();
    const clientX = e.touches?.[0]?.clientX ?? e.clientX;
    const ratio = (clientX - rect.left) / rect.width;
    const svgX = ratio * w;
    if (svgX < padL - 5 || svgX > w - padR + 5) {
      setHoverIdx(null);
      return;
    }
    const i = Math.round((svgX - padL) / xStep);
    if (i < 0 || i >= maxLen) {
      setHoverIdx(null);
      return;
    }
    setHoverIdx(i);
  };

  // Resolve the A/B data points at the hovered index (either may be missing).
  const aPt = hoverIdx !== null && aMonthly[hoverIdx] ? aMonthly[hoverIdx] : null;
  const bPt = hoverIdx !== null && bMonthly[hoverIdx] ? bMonthly[hoverIdx] : null;
  const hoverX = hoverIdx !== null ? padL + hoverIdx * xStep : null;
  const yieldToY = (v) => valToY(v);
  const aDotY = aPt ? yieldToY(aPt.yield_pct || 0) : null;
  const bDotY = bPt ? yieldToY(bPt.yield_pct || 0) : null;
  // Prefer A's date for the header; fall back to B if A doesn't cover the idx.
  const hoverDate = (aPt || bPt)?.date?.slice(0, 7);

  // Per-ticker stats for the scale legend. Surfaces "Y-axis: 0% → max"
  // plus each side's individual peak so users instantly see why a
  // small-yield ticker (e.g. PEB ~0.3%) looks nearly flat when paired
  // against a multi-percent stock (e.g. NATH ~2.8%). When the larger
  // peak is ≥3× the smaller one, badge a 🔍 "low yield looks flat"
  // hint so the visual disparity isn't read as a chart bug.
  const aPositive = aMonthly.map((d) => d.yield_pct || 0).filter((v) => v > 0);
  const bPositive = bMonthly.map((d) => d.yield_pct || 0).filter((v) => v > 0);
  const aMaxYield = aPositive.length ? Math.max(...aPositive) : null;
  const bMaxYield = bPositive.length ? Math.max(...bPositive) : null;
  const scaleSkew =
    aMaxYield && bMaxYield ? Math.max(aMaxYield, bMaxYield) / Math.min(aMaxYield, bMaxYield) : 1;

  return (
    <div className="rounded-lg border bg-card p-3 sm:p-4 relative" data-testid="compare-yield-chart">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">
          Yield % History (3 Years)
        </h3>
        {/* Round 89c — Y-axis scale legend so users see why a low-yield
            ticker looks flat next to a high-yield one. Shows the chart's
            true 0% → yMax range plus each side's individual peak. */}
        {(aMaxYield !== null || bMaxYield !== null) && (
          <div
            className="flex items-center gap-2 text-[10px] font-mono leading-tight"
            data-testid="compare-yield-scale-legend"
          >
            <span className="text-muted-foreground">
              {useLog ? (
                <>
                  Y-axis (log):{' '}
                  <span className="font-bold">
                    {yMinLog < 1 ? yMinLog.toFixed(2) : yMinLog.toFixed(1)}%
                  </span>{' '}
                  → <span className="font-bold">{yMaxLog.toFixed(1)}%</span>
                </>
              ) : (
                <>
                  Y-axis: <span className="font-bold">0%</span> →{' '}
                  <span className="font-bold">{yMax.toFixed(yMax < 1 ? 2 : 1)}%</span>
                </>
              )}
            </span>
            <span className="text-muted-foreground/40">·</span>
            <span className="flex items-center gap-2">
              {aTicker && aMaxYield !== null && (
                <span className="flex items-center gap-1">
                  <span
                    className="inline-block w-1.5 h-1.5 rounded-full"
                    style={{ backgroundColor: SLOT_STYLES.A.lineStroke }}
                  />
                  <span className="font-semibold" style={{ color: SLOT_STYLES.A.lineStroke }}>
                    {aTicker}
                  </span>
                  <span className="text-muted-foreground">peak</span>
                  <span className="font-bold">{aMaxYield.toFixed(2)}%</span>
                </span>
              )}
              {bTicker && bMaxYield !== null && (
                <span className="flex items-center gap-1">
                  <span
                    className="inline-block w-1.5 h-1.5 rounded-full"
                    style={{ backgroundColor: SLOT_STYLES.B.lineStroke }}
                  />
                  <span className="font-semibold" style={{ color: SLOT_STYLES.B.lineStroke }}>
                    {bTicker}
                  </span>
                  <span className="text-muted-foreground">peak</span>
                  <span className="font-bold">{bMaxYield.toFixed(2)}%</span>
                </span>
              )}
            </span>
            {scaleSkew >= 3 && (
              <span
                className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-amber-700 dark:text-amber-300 bg-amber-500/10 border border-amber-500/30"
                title={`Scale skew: ${scaleSkew.toFixed(1)}× — the smaller-yield line is compressed against 0% so its month-to-month moves look almost flat. This is the true ratio, not a chart bug.`}
                data-testid="compare-yield-skew-warning"
              >
                {scaleSkew.toFixed(1)}× skew
              </span>
            )}
            {/* Round 89d — Log-scale toggle, surfaced only when the
                skew warning fires. Flipping log10 reshapes the Y axis
                so a 0.3% line and a 2.8% line both occupy useful
                vertical space, revealing month-to-month movement
                that the linear chart squashes against 0%. */}
            {scaleSkew >= 3 && (
              <button
                type="button"
                onClick={() => setLogScale((v) => !v)}
                className={`ml-0.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-semibold transition-colors ${
                  logScale
                    ? 'bg-sky-500 border-sky-600 text-white hover:bg-sky-600'
                    : 'border-border bg-muted/30 hover:bg-muted/60 text-muted-foreground'
                }`}
                title={
                  logScale
                    ? 'Linear scale: equal pixel = equal % point. Click to switch back.'
                    : 'Log scale: equal pixel = equal % ratio. Reveals month-to-month moves on the smaller-yield ticker without losing the larger one.'
                }
                aria-pressed={logScale}
                data-testid="compare-yield-log-toggle"
              >
                <span
                  className={`inline-block w-1.5 h-1.5 rounded-full ${
                    logScale ? 'bg-white' : 'bg-muted-foreground/50'
                  }`}
                />
                Log
              </button>
            )}
          </div>
        )}
      </div>
      <div className="relative">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${w} ${h}`}
          className="w-full h-56"
          preserveAspectRatio="none"
          onMouseMove={handleMove}
          onMouseLeave={() => setHoverIdx(null)}
          onTouchStart={handleMove}
          onTouchMove={handleMove}
        >
          {/* grid lines */}
          {yLabels.map((l, i) => {
            const y = l.y;
            return (
              <g key={i}>
                <line x1={padL} y1={y} x2={w - padR} y2={y} stroke={gridColor} strokeWidth="1" />
                <text x={padL - 4} y={y + 3} fontSize="9" textAnchor="end" fill={textColor} fontFamily="monospace">
                  {l.label}
                </text>
              </g>
            );
          })}
          {/* x labels — first/middle/last. Anchor each label so it stays
              inside the chart area: "start" aligns the left edge to the
              leftmost data point, "end" aligns the right edge to the
              rightmost data point, "middle" centers the middle label.
              Without this, middle-anchoring the last label clips the
              final month ("2026-04") at the SVG's right edge. */}
          {xLabels.map((p, i) => {
            const x = padL + p.i * xStep;
            const anchor = i === 0 ? 'start' : i === xLabels.length - 1 ? 'end' : 'middle';
            return (
              <text key={i} x={x} y={h - 8} fontSize="9" textAnchor={anchor} fill={textColor} fontFamily="monospace">
                {p.label}
              </text>
            );
          })}
          {/* A line */}
          {aPath && <path d={aPath} fill="none" stroke={SLOT_STYLES.A.lineStroke} strokeWidth="2" vectorEffect="non-scaling-stroke" />}
          {/* B line */}
          {bPath && <path d={bPath} fill="none" stroke={SLOT_STYLES.B.lineStroke} strokeWidth="2" vectorEffect="non-scaling-stroke" />}
          {/* crossover diamonds — marked where A's yield crossed B's */}
          {crossovers.map((c) => (
            <g key={c.date}>
              <polygon
                points={`${c.x},${c.y - 5} ${c.x + 5},${c.y} ${c.x},${c.y + 5} ${c.x - 5},${c.y}`}
                fill="#facc15"
                stroke={isDark ? '#0a0a0a' : 'white'}
                strokeWidth="1.2"
                vectorEffect="non-scaling-stroke"
              >
                <title>{`Yield crossover — ${c.date.slice(0, 7)}`}</title>
              </polygon>
            </g>
          ))}
          {/* hover guide + dots */}
          {hoverX !== null && (
            <>
              <line
                x1={hoverX}
                y1={padT}
                x2={hoverX}
                y2={h - padB}
                stroke={textColor}
                strokeWidth="1"
                strokeDasharray="3 3"
                vectorEffect="non-scaling-stroke"
                opacity="0.5"
              />
              {aDotY !== null && (
                <circle cx={hoverX} cy={aDotY} r="4" fill={SLOT_STYLES.A.lineStroke} stroke="white" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
              )}
              {bDotY !== null && (
                <circle cx={hoverX} cy={bDotY} r="4" fill={SLOT_STYLES.B.lineStroke} stroke="white" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
              )}
            </>
          )}
        </svg>
        {/* Floating tooltip — positioned in DOM space (percentages) so it
            avoids the non-uniform SVG scaling issues. Clamped horizontally
            by `useTipClamp` so it never overflows the modal edge when
            hovering the last data point (2026-04). */}
        {hoverIdx !== null && (aPt || bPt) && (
          <div
            ref={tipRef}
            className={`pointer-events-none absolute top-0 z-20 ${isDark ? 'bg-zinc-800' : 'bg-zinc-900'} text-white rounded-md px-2 py-1.5 text-[11px] font-mono shadow-lg whitespace-nowrap`}
            style={{
              left: `${((hoverX - padL) / (w - padL - padR)) * 100}%`,
              transform: `translateX(calc(-50% + ${tipShiftX}px))`,
            }}
            data-testid="compare-yield-tooltip"
          >
            <div className="font-semibold text-center text-muted-foreground text-[10px]">{hoverDate}</div>
            {aPt && (
              <div>
                <span style={{ color: SLOT_STYLES.A.lineStroke }}>●</span> {aTicker}: {(aPt.yield_pct || 0).toFixed(2)}%
              </div>
            )}
            {bPt && (
              <div>
                <span style={{ color: SLOT_STYLES.B.lineStroke }}>●</span> {bTicker}: {(bPt.yield_pct || 0).toFixed(2)}%
              </div>
            )}
          </div>
        )}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-4 text-[11px]">
        {aTicker && (
          <div className="flex items-center gap-1.5" title={aMonthly.length === 0 ? 'No yield history available for this ticker yet. Refresh in a few minutes.' : undefined}>
            <span
              className="inline-block w-4 h-0.5"
              style={{
                backgroundColor: SLOT_STYLES.A.lineStroke,
                opacity: aMonthly.length === 0 ? 0.3 : 1,
              }}
            />
            <span
              className={`font-mono font-semibold ${aMonthly.length === 0 ? 'text-muted-foreground line-through decoration-dotted' : ''}`}
              data-testid={`compare-yield-legend-${aTicker}`}
            >
              {aTicker} Yield
              {aMonthly.length === 0 && (
                <span className="ml-1 not-italic text-[10px] font-normal opacity-70 no-underline">
                  (no data)
                </span>
              )}
            </span>
          </div>
        )}
        {bTicker && (
          <div className="flex items-center gap-1.5" title={bMonthly.length === 0 ? 'No yield history available for this ticker yet. Refresh in a few minutes.' : undefined}>
            <span
              className="inline-block w-4 h-0.5"
              style={{
                backgroundColor: SLOT_STYLES.B.lineStroke,
                opacity: bMonthly.length === 0 ? 0.3 : 1,
              }}
            />
            <span
              className={`font-mono font-semibold ${bMonthly.length === 0 ? 'text-muted-foreground line-through decoration-dotted' : ''}`}
              data-testid={`compare-yield-legend-${bTicker}`}
            >
              {bTicker} Yield
              {bMonthly.length === 0 && (
                <span className="ml-1 not-italic text-[10px] font-normal opacity-70 no-underline">
                  (no data)
                </span>
              )}
            </span>
          </div>
        )}
        {crossovers.length > 0 && (
          <div
            className="flex items-center gap-1.5"
            data-testid="compare-yield-crossover-count"
            title={`Yield crossovers: ${crossovers.map((c) => c.date.slice(0, 7)).join(', ')}`}
          >
            <span className="text-amber-500 text-sm leading-none">◆</span>
            <span className="font-semibold text-muted-foreground">
              {crossovers.length} yield crossover{crossovers.length !== 1 ? 's' : ''}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Recently Compared — persists last 5 pairs across sessions so users can
// jump back into the same face-off without re-typing. Pairs with only one
// slot filled are also remembered (e.g. launched from StockModal shortcut).
//
// Storage strategy:
//   - Logged in  → MongoDB-backed `/api/compare-history` (cross-device).
//   - Logged out → browser localStorage (single-device, the original
//                  fallback before the API existed).
// The component picks the right path based on the `user` prop; both
// surfaces ultimately produce the same `[{a, b}, …]` shape.
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Round 96b (Feb 2026) — Merged Revenue / EPS bar chart for non-dividend
// pairs. The dividend growth + yield charts above are useless when one
// or both tickers don't pay a dividend, so we swap in these two
// growth-stock-appropriate charts instead. Both pull from the same
// `/api/stocks/{ticker}/triangle` payload already fetched in fetchSlot.
//
// Mixed units: Massive's snapshot sometimes returns raw dollars (unit
// "") and sometimes pre-scaled (unit "M"/"B"). Normalize to raw USD
// for the Y-scale and use a single auto-unit formatter for labels.
// ---------------------------------------------------------------------------
const TRI_UNIT_MULT = { '': 1, M: 1e6, B: 1e9, T: 1e12, k: 1e3, K: 1e3 };
function triToRawUsd(entry) {
  if (!entry || entry.value == null) return 0;
  const m = TRI_UNIT_MULT[entry.unit] != null ? TRI_UNIT_MULT[entry.unit] : 1;
  return entry.value * m;
}
function fmtUsdAutoCompare(usd) {
  if (usd == null || Number.isNaN(usd)) return '—';
  const abs = Math.abs(usd);
  const sign = usd < 0 ? '-' : '';
  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(1)}K`;
  return `${sign}$${abs.toFixed(0)}`;
}
function fmtEpsCompare(v) {
  if (v == null) return '—';
  const sign = v < 0 ? '-' : '';
  return `${sign}$${Math.abs(v).toFixed(2)}`;
}

function MergedFinancialChart({ a, b, aTicker, bTicker, metric }) {
  // metric: 'revenue' | 'eps'
  const aSeries = a?.[metric]?.annual || [];
  const bSeries = b?.[metric]?.annual || [];
  if (aSeries.length === 0 && bSeries.length === 0) return null;

  const isRevenue = metric === 'revenue';
  const toRaw = isRevenue ? triToRawUsd : (d) => d?.value || 0;
  const fmt = isRevenue ? fmtUsdAutoCompare : fmtEpsCompare;

  const lookupA = Object.fromEntries(aSeries.map((d) => [d.year, toRaw(d)]));
  const lookupB = Object.fromEntries(bSeries.map((d) => [d.year, toRaw(d)]));
  const years = Array.from(new Set([...aSeries.map((d) => d.year), ...bSeries.map((d) => d.year)])).sort();

  // Cap to the most recent 8 years so the chart stays scannable on
  // small screens. Older history is mostly noise for growth stocks.
  const visibleYears = years.slice(-8);

  const allValues = visibleYears.flatMap((y) => [lookupA[y] || 0, lookupB[y] || 0]);
  const maxV = Math.max(...allValues);
  const minV = Math.min(...allValues, 0);
  const range = Math.max(Math.abs(maxV), Math.abs(minV)) * 1.2 || 1;

  const aCagr = a?.[metric]?.cagr_pct;
  const bCagr = b?.[metric]?.cagr_pct;

  const yLabels = [1, 0.75, 0.5, 0.25, 0].map((frac) => fmt(range * frac));
  const title = isRevenue ? 'Annual Revenue Comparison' : 'Diluted EPS Comparison';
  const testId = isRevenue ? 'compare-revenue-chart' : 'compare-eps-chart';

  return (
    <div className="rounded-lg border bg-card p-3 sm:p-4" data-testid={testId}>
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">
          {title}
        </h3>
        <div className="text-[10px] text-muted-foreground flex gap-3">
          {aTicker && aCagr != null && (
            <span data-testid={`compare-${metric}-cagr-a`}>
              <span className="font-mono font-semibold">{aTicker}</span>{' '}
              CAGR <span className={aCagr >= 0 ? 'text-emerald-500' : 'text-rose-500'}>
                {aCagr >= 0 ? '+' : ''}{aCagr.toFixed(1)}%
              </span>
            </span>
          )}
          {bTicker && bCagr != null && (
            <span data-testid={`compare-${metric}-cagr-b`}>
              <span className="font-mono font-semibold">{bTicker}</span>{' '}
              CAGR <span className={bCagr >= 0 ? 'text-emerald-500' : 'text-rose-500'}>
                {bCagr >= 0 ? '+' : ''}{bCagr.toFixed(1)}%
              </span>
            </span>
          )}
        </div>
      </div>
      <div className="flex items-end">
        <div className="flex h-56 flex-col justify-between pr-2 shrink-0">
          {yLabels.map((l, i) => (
            <span key={i} className="text-[9px] font-mono text-muted-foreground leading-none">{l}</span>
          ))}
        </div>
        <div className="flex-1 flex items-end gap-3 sm:gap-6 h-56 border-b border-l border-border/40 pl-2 pr-1">
          {visibleYears.map((y) => {
            const aV = lookupA[y] || 0;
            const bV = lookupB[y] || 0;
            const aPct = range > 0 ? (Math.abs(aV) / range) * 100 : 0;
            const bPct = range > 0 ? (Math.abs(bV) / range) * 100 : 0;
            return (
              <div key={y} className="flex-1 flex flex-col items-center justify-end h-full min-w-0">
                <div className="flex items-end gap-1 sm:gap-1.5 h-full w-full justify-center">
                  {aSeries.length > 0 && (
                    <HoverBar
                      label={`${aTicker} ${y}: ${fmt(aV)}`}
                      className="flex-1 max-w-[28px] sm:max-w-[44px] rounded-t-sm transition-all"
                      style={{ height: `${Math.max(aPct, 2)}%`, backgroundColor: SLOT_STYLES.A.barFill }}
                    />
                  )}
                  {bSeries.length > 0 && (
                    <HoverBar
                      label={`${bTicker} ${y}: ${fmt(bV)}`}
                      className="flex-1 max-w-[28px] sm:max-w-[44px] rounded-t-sm transition-all"
                      style={{ height: `${Math.max(bPct, 2)}%`, backgroundColor: SLOT_STYLES.B.barFill }}
                    />
                  )}
                </div>
                <span className="mt-1 text-[10px] font-mono text-muted-foreground">{y}</span>
              </div>
            );
          })}
        </div>
      </div>
      <div className="mt-3 flex items-center gap-4 text-[11px]">
        {aTicker && aSeries.length > 0 && (
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: SLOT_STYLES.A.barFill }} />
            <span className="font-mono font-semibold">{aTicker}</span>
          </div>
        )}
        {bTicker && bSeries.length > 0 && (
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: SLOT_STYLES.B.barFill }} />
            <span className="font-mono font-semibold">{bTicker}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Round 99a (Jul 2026) — AltMetricChart. Renders a per-slot mini bar chart
// for REITs (FFO), BDCs (NII), MLPs (DCF), or CEFs (NAV note). Unlike the
// EPS chart above, this can't share an axis across slots because the two
// tickers might report different metrics (REIT FFO vs BDC NII) — so we
// render one card per slot side-by-side and let each self-format.
//
// Data source: `/api/stocks/{ticker}/alt-metrics` returns
//   { ticker, asset_class, primary_metric,
//     annual: [{year, period_end, value}], cagr_pct }
// where value is raw USD.
// ---------------------------------------------------------------------------
function CefNavPanel({ ticker }) {
  const [snap, setSnap] = useState(undefined);
  const [series, setSeries] = useState([]);
  useEffect(() => {
    let cancelled = false;
    setSnap(undefined);
    Promise.all([
      axios.get(`${API}/stocks/${ticker}/cef-nav`).catch(() => ({ data: null })),
      axios.get(`${API}/stocks/${ticker}/cef-nav-series`).catch(() => ({ data: { points: [] } })),
    ]).then(([a, b]) => {
      if (cancelled) return;
      setSnap(a.data || null);
      setSeries((b.data?.points || []).filter((p) => p.discount_pct != null));
    });
    return () => { cancelled = true; };
  }, [ticker]);

  const pct = (v, sign = true) => (v == null ? '—' : `${sign && v > 0 ? '+' : ''}${Number(v).toFixed(1)}%`);
  const tone = (v) => (v == null ? 'text-muted-foreground' : v < 0 ? 'text-emerald-500' : 'text-rose-500');
  const price = snap?.price ?? null;
  const disc = snap?.discount_pct_live ?? snap?.discount_pct ?? null;

  let path = null;
  let yMin = 0; let yMax = 0;
  if (series.length >= 2) {
    const vals = series.map((p) => p.discount_pct);
    yMin = Math.min(...vals); yMax = Math.max(...vals);
    const span = yMax - yMin || 1;
    path = series.map((p, i) => {
      const x = (i / (series.length - 1)) * 100;
      const y = 100 - ((p.discount_pct - yMin) / span) * 100;
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`;
    }).join(' ');
  }

  return (
    <div className="rounded-lg border bg-card p-3 sm:p-4" data-testid={`compare-altmetric-${ticker}`}>
      <div className="flex items-baseline justify-between mb-2 gap-2">
        <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">{ticker} · NAV (CEF)</h3>
        {snap?.updated_at && <span className="text-[9px] text-muted-foreground">as of {new Date(snap.updated_at).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}</span>}
      </div>
      {snap === undefined && <div className="text-[11px] text-muted-foreground">Loading NAV…</div>}
      {snap === null && <div className="text-[11px] text-muted-foreground" data-testid={`compare-cef-nav-empty-${ticker}`}>No NAV data on file for {ticker} yet. Use the Dividend Growth + Yield charts above for the CEF comparison.</div>}
      {snap && (
        <>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-x-3 gap-y-2" data-testid={`compare-cef-nav-stats-${ticker}`}>
            {[
              ['NAV', snap.nav != null ? `$${Number(snap.nav).toFixed(2)}` : '—', 'text-foreground'],
              ['Price', price != null ? `$${price.toFixed(2)}` : '—', 'text-foreground'],
              [disc != null && disc > 0 ? 'Premium' : 'Discount', pct(disc), tone(disc)],
              ['52W Avg', pct(snap.discount_52wk_avg), tone(snap.discount_52wk_avg)],
              ['YTD NAV', pct(snap.ytd_nav_return), snap.ytd_nav_return == null ? 'text-muted-foreground' : snap.ytd_nav_return >= 0 ? 'text-emerald-500' : 'text-rose-500'],
            ].map(([label, value, cls]) => (
              <div key={label}>
                <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</div>
                <div className={`font-mono text-sm font-semibold ${cls}`}>{value}</div>
              </div>
            ))}
          </div>
          {disc != null && snap.discount_52wk_avg != null && (
            <p className="mt-2 text-[10px] text-muted-foreground leading-snug">
              {disc < snap.discount_52wk_avg
                ? `Discount is ${(snap.discount_52wk_avg - disc).toFixed(1)} pts wider than its 52-week average — cheaper than usual vs. NAV.`
                : `Discount is ${(disc - snap.discount_52wk_avg).toFixed(1)} pts tighter than its 52-week average — pricier than usual vs. NAV.`}
            </p>
          )}
          {path ? (
            <div className="mt-3" data-testid={`compare-cef-nav-spark-${ticker}`}>
              <div className="flex items-center justify-between text-[9px] uppercase tracking-wider text-muted-foreground mb-1">
                <span>Discount to NAV · {series.length} daily snapshots</span>
                <span className="font-mono normal-case">{pct(yMin)} → {pct(yMax)}</span>
              </div>
              <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-14">
                <path d={path} fill="none" stroke="currentColor" strokeWidth="1.5" vectorEffect="non-scaling-stroke" className="text-violet-500" />
              </svg>
              <div className="flex justify-between text-[9px] text-muted-foreground font-mono">
                <span>{series[0].date}</span><span>{series[series.length - 1].date}</span>
              </div>
            </div>
          ) : (
            <p className="mt-2 text-[10px] text-muted-foreground">Daily NAV snapshots are still accumulating — a discount-history line appears once there are 2+ days on file.</p>
          )}
          {snap.strategy && <div className="mt-2 text-[10px] text-muted-foreground truncate" title={snap.strategy}>Strategy: {snap.strategy}</div>}
        </>
      )}
    </div>
  );
}

function SingleMetricBarChart({ ticker, altMetrics }) {
  if (!altMetrics || !altMetrics.asset_class) return null;
  const metric = altMetrics.primary_metric || '';
  const annual = altMetrics.annual || [];
  const cagr = altMetrics.cagr_pct;

  // CEF case — no annual NAV series exists; show the live NAV snapshot
  // + daily discount history instead.
  if (altMetrics.asset_class === 'CEF') {
    return <CefNavPanel ticker={ticker} />;
  }

  if (annual.length === 0) {
    return (
      <div className="rounded-lg border bg-card p-3 sm:p-4" data-testid={`compare-altmetric-${ticker}`}>
        <div className="flex items-baseline justify-between mb-2">
          <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">
            {ticker} · {metric}
          </h3>
        </div>
        <div className="text-[11px] text-muted-foreground leading-tight">
          No {metric} history available yet — Massive&apos;s income statement returned no usable rows.
        </div>
      </div>
    );
  }

  const visible = [...annual].sort((x, y) => (x.year || 0) - (y.year || 0)).slice(-8);
  const maxV = Math.max(...visible.map((d) => d.value || 0));
  const minV = Math.min(...visible.map((d) => d.value || 0), 0);
  const range = Math.max(Math.abs(maxV), Math.abs(minV)) * 1.2 || 1;
  const yLabels = [1, 0.75, 0.5, 0.25, 0].map((frac) => fmtUsdAutoCompare(range * frac));

  return (
    <div className="rounded-lg border bg-card p-3 sm:p-4" data-testid={`compare-altmetric-${ticker}`}>
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">
          {ticker} · Annual {metric}
        </h3>
        {cagr != null && (
          <div className="text-[10px] text-muted-foreground" data-testid={`compare-altmetric-cagr-${ticker}`}>
            CAGR{' '}
            <span className={cagr >= 0 ? 'text-emerald-500' : 'text-rose-500'}>
              {cagr >= 0 ? '+' : ''}{cagr.toFixed(1)}%
            </span>
          </div>
        )}
      </div>
      <div className="flex gap-3">
        <div className="flex flex-col justify-between text-[9px] text-muted-foreground" style={{ height: 120 }}>
          {yLabels.map((lbl, i) => (
            <span key={i} className="tabular-nums leading-none">{lbl}</span>
          ))}
        </div>
        <div className="flex-1 flex items-end gap-1" style={{ height: 120 }}>
          {visible.map((d, i) => {
            const h = range > 0 ? Math.max(1, (d.value / range) * 100) : 0;
            return (
              <div
                key={d.year || i}
                className="flex-1 flex flex-col items-center h-full justify-end"
              >
                <div
                  className="w-full rounded-t bg-violet-500/70 hover:bg-violet-500 transition-colors"
                  style={{ height: `${h}%`, minHeight: 2 }}
                  title={`${d.year}: ${fmtUsdAutoCompare(d.value)}`}
                />
                <span className="mt-1 text-[9px] text-muted-foreground tabular-nums leading-none">
                  {d.year != null ? `'${String(d.year).slice(-2)}` : ''}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}


const HISTORY_KEY = 'divitrack.compare.history.v1';
const HISTORY_MAX = 5;

// ---------------------------------------------------------------------------
// Round 96b (Feb 2026) — Universal Price Performance bar chart. Unlike the
// Revenue/EPS charts above (which only work when Massive returned income-
// statement data — typically only common stocks, not ETFs), this chart
// works for ANY ticker that has a price feed: ETFs, REITs, BDCs, CEFs,
// MLPs, ADRs, and plain common stocks. Used as the guaranteed fallback
// when one or both compare slots is an ETF (no rev/eps data) or non-div.
//
// Data source: `stock.perf_1m`, `perf_6m`, `perf_1y` from the stock
// detail endpoint, plus `cagr_5yr` extrapolated to 5y cumulative.
// ---------------------------------------------------------------------------
function MergedPerfChart({ a, b, aTicker, bTicker }) {
  const aDet = a?.detail || null;
  const bDet = b?.detail || null;
  if (!aDet && !bDet) return null;

  // 5Y cumulative ≈ (1 + cagr/100)^5 - 1, expressed as a percentage.
  // Round P132b (Feb 2026) — Cap CAGR at 20% (see the twin
  // implementation later in this file for the full rationale).
  const fiveYrCum = (cagr) => {
    if (cagr == null || Number.isNaN(cagr)) return null;
    const capped = Math.min(Number(cagr), 20);
    return (Math.pow(1 + capped / 100, 5) - 1) * 100;
  };

  const periods = [
    { key: '1m',  label: '1 Month',  aVal: aDet?.perf_1m,  bVal: bDet?.perf_1m },
    { key: '6m',  label: '6 Months', aVal: aDet?.perf_6m,  bVal: bDet?.perf_6m },
    // Round 96b (Feb 2026) — YTD row. Computed in fetchSlot from the
    // daily price history (Jan 1 close vs latest close). High signal
    // during volatile years — the timeframe retail investors ask
    // about most often.
    { key: 'ytd', label: 'YTD',      aVal: aDet?._ytd_return, bVal: bDet?._ytd_return },
    { key: '1y',  label: '1 Year',   aVal: aDet?.perf_1y,  bVal: bDet?.perf_1y },
    { key: '5y',  label: '5 Year (cum.)', aVal: fiveYrCum(aDet?.cagr_5yr), bVal: fiveYrCum(bDet?.cagr_5yr) },
  ];

  // Drop empty rows so a missing 5Y CAGR doesn't render a blank column.
  const rows = periods.filter((p) => p.aVal != null || p.bVal != null);
  if (rows.length === 0) return null;

  const allVals = rows.flatMap((p) => [p.aVal || 0, p.bVal || 0]);
  const maxV = Math.max(...allVals);
  const minV = Math.min(...allVals);
  const range = Math.max(Math.abs(maxV), Math.abs(minV)) * 1.2 || 1;

  const fmtPctLocal = (v) => {
    if (v == null || Number.isNaN(v)) return '—';
    const s = v >= 0 ? '+' : '';
    return `${s}${v.toFixed(1)}%`;
  };

  return (
    <div className="rounded-lg border bg-card p-3 sm:p-4" data-testid="compare-perf-chart">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">
          Price Performance Comparison
        </h3>
        <div className="text-[10px] text-muted-foreground">
          Cumulative price change · ETFs/REITs/BDCs supported
        </div>
      </div>
      <div className="space-y-2">
        {rows.map((p) => {
          // Center-line layout: positive bars go right of center, negative
          // go left. Each side scaled to `range` for parity across rows.
          const aPct = p.aVal != null ? (p.aVal / range) * 50 : 0;
          const bPct = p.bVal != null ? (p.bVal / range) * 50 : 0;
          return (
            <div key={p.key} className="flex items-center gap-2 text-[11px]">
              <div className="w-20 shrink-0 text-muted-foreground text-right pr-1">{p.label}</div>
              <div className="flex-1 flex flex-col gap-0.5">
                {/* A bar */}
                <div className="relative h-3 bg-muted/30 rounded-sm overflow-hidden">
                  <div className="absolute top-0 bottom-0 left-1/2 w-px bg-border/40" />
                  {p.aVal != null && (
                    <HoverBar
                      label={`${aTicker} ${p.label}: ${fmtPctLocal(p.aVal)}`}
                      className="absolute top-0 bottom-0"
                      style={{
                        backgroundColor: SLOT_STYLES.A.barFill,
                        left: p.aVal >= 0 ? '50%' : `${50 + aPct}%`,
                        width: `${Math.abs(aPct)}%`,
                      }}
                    />
                  )}
                </div>
                {/* B bar */}
                <div className="relative h-3 bg-muted/30 rounded-sm overflow-hidden">
                  <div className="absolute top-0 bottom-0 left-1/2 w-px bg-border/40" />
                  {p.bVal != null && (
                    <HoverBar
                      label={`${bTicker} ${p.label}: ${fmtPctLocal(p.bVal)}`}
                      className="absolute top-0 bottom-0"
                      style={{
                        backgroundColor: SLOT_STYLES.B.barFill,
                        left: p.bVal >= 0 ? '50%' : `${50 + bPct}%`,
                        width: `${Math.abs(bPct)}%`,
                      }}
                    />
                  )}
                </div>
              </div>
              <div className="w-32 shrink-0 flex gap-2 font-mono">
                <span
                  className={`w-14 text-right ${p.aVal != null && p.aVal >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}
                  data-testid={`perf-a-${p.key}`}
                >
                  {fmtPctLocal(p.aVal)}
                </span>
                <span
                  className={`w-14 text-right ${p.bVal != null && p.bVal >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}
                  data-testid={`perf-b-${p.key}`}
                >
                  {fmtPctLocal(p.bVal)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex items-center gap-4 text-[11px]">
        {aTicker && (
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: SLOT_STYLES.A.barFill }} />
            <span className="font-mono font-semibold">{aTicker}</span>
          </div>
        )}
        {bTicker && (
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: SLOT_STYLES.B.barFill }} />
            <span className="font-mono font-semibold">{bTicker}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function loadHistory() {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((p) => p && p.a) : [];
  } catch {
    return [];
  }
}

function saveHistoryPair(a, b) {
  if (!a) return;
  const pair = { a: a.toUpperCase(), b: b ? b.toUpperCase() : null };
  // Dedupe:
  //   1. Exact same pair (or mirrored) collapses to one entry at the top.
  //   2. When saving a full pair (both slots), wipe any prior "solo" for
  //      either ticker — the richer pair supersedes it.
  const existing = loadHistory().filter((p) => {
    if (p.a === pair.a && p.b === pair.b) return false;
    if (p.a === pair.b && p.b === pair.a) return false;
    if (pair.b && !p.b && (p.a === pair.a || p.a === pair.b)) return false;
    return true;
  });
  const next = [pair, ...existing].slice(0, HISTORY_MAX);
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
  } catch {
    // localStorage may be disabled (private mode) — feature degrades silently.
  }
}

// Backend-backed counterparts. Same return / argument shape as the
// localStorage helpers above — failures degrade silently to an empty
// list so a slow API never breaks the compare flow.
async function loadHistoryRemote() {
  try {
    const r = await axios.get(`${API}/compare-history`, { withCredentials: true });
    return Array.isArray(r.data) ? r.data.filter((p) => p && p.a) : [];
  } catch {
    return [];
  }
}

async function saveHistoryPairRemote(a, b) {
  if (!a) return;
  try {
    await axios.post(
      `${API}/compare-history`,
      { a: a.toUpperCase(), b: b ? b.toUpperCase() : null },
      { withCredentials: true },
    );
  } catch {
    // 401 / 5xx — fall through; the localStorage copy still works on
    // this device until the user logs back in.
  }
}

// ---------------------------------------------------------------------------
// Main modal
// ---------------------------------------------------------------------------

/**
 * Build a plain-English one-liner summarising the matchup. Rules:
 *  - If both yields known, whoever pays more gets "pays X× the yield".
 *  - If both 1-year returns known, whoever returned more gets "outperformed
 *    over 1Y (+X% vs +Y%)".
 *  - Joined with "but" when the yield-winner lost on price (classic
 *    income-vs-total-return tradeoff) or "and" when the same side won both.
 *  - Returns null when we don't have enough data to say anything meaningful.
 */
function buildSummary(a, b, aDetail, bDetail) {
  if (!aDetail || !bDetail || !a?.ticker || !b?.ticker) return null;
  const aT = a.ticker;
  const bT = b.ticker;
  const aY = aDetail.dividend_yield;
  const bY = bDetail.dividend_yield;

  const parts = [];
  let yieldWinner = null;

  if (typeof aY === 'number' && typeof bY === 'number' && aY > 0 && bY > 0) {
    if (aY === bY) {
      parts.push(`${aT} and ${bT} pay nearly identical yields (${aY.toFixed(2)}%)`);
    } else {
      const hi = aY > bY ? { t: aT, v: aY } : { t: bT, v: bY };
      const lo = aY > bY ? { t: bT, v: bY } : { t: aT, v: aY };
      const ratio = lo.v > 0 ? hi.v / lo.v : 0;
      yieldWinner = hi.t;
      if (ratio >= 1.3) {
        parts.push(`${hi.t} pays ${ratio.toFixed(1)}× the yield (${hi.v.toFixed(2)}% vs ${lo.v.toFixed(2)}%)`);
      } else {
        parts.push(`${hi.t} yields slightly more (${hi.v.toFixed(2)}% vs ${lo.v.toFixed(2)}%)`);
      }
    }
  }

  // Round 96b (Feb 2026) — Auto-select the timeframe with the largest
  // absolute spread between the two tickers (1M, 6M, YTD, 1Y, 5Y
  // cumulative). For non-dividend pairs and pairs that include an ETF
  // the 1Y window is often noisy — surfacing the most-differentiated
  // window puts the real signal in the takeaway. Falls back to 1Y
  // when richer perf data isn't available so dividend pairs keep
  // working exactly as before.
  // Round P132b (Feb 2026) — Cap the CAGR at 20% before compounding
  // for the 5-yr cumulative display. Prevents CEF/BDC specials-inflated
  // "49.8%" figures from producing 655%+ cumulative growth numbers
  // that mislead comparisons. Realistic ceiling per the app-wide
  // convention (DDM uses 20%, Payback timeline uses 15%, Chowder
  // uses 15%).
  const fiveYrCum = (cagr) => {
    if (cagr == null || Number.isNaN(cagr)) return null;
    const capped = Math.min(Number(cagr), 20);
    return (Math.pow(1 + capped / 100, 5) - 1) * 100;
  };
  const tfCandidates = [
    { key: '1Y',   label: '1Y',   aV: aDetail.perf_1y,   bV: bDetail.perf_1y },
    { key: 'YTD',  label: 'YTD',  aV: aDetail._ytd_return, bV: bDetail._ytd_return },
    { key: '5Y',   label: '5Y cum.', aV: fiveYrCum(aDetail.cagr_5yr), bV: fiveYrCum(bDetail.cagr_5yr) },
    { key: '6M',   label: '6M',   aV: aDetail.perf_6m,   bV: bDetail.perf_6m },
    { key: '1M',   label: '1M',   aV: aDetail.perf_1m,   bV: bDetail.perf_1m },
  ];
  const validTfs = tfCandidates.filter(
    (t) => typeof t.aV === 'number' && typeof t.bV === 'number',
  );
  validTfs.sort((x, y) => Math.abs(y.aV - y.bV) - Math.abs(x.aV - x.bV));
  const bestTf = validTfs[0] || null;

  if (bestTf) {
    const winner = bestTf.aV > bestTf.bV
      ? { t: aT, v: bestTf.aV, l: bestTf.bV }
      : { t: bT, v: bestTf.bV, l: bestTf.aV };
    const sign = (n) => (n >= 0 ? '+' : '') + n.toFixed(2) + '%';
    const spread = Math.abs(bestTf.aV - bestTf.bV);
    if (spread < 0.5) {
      parts.push(`both delivered similar ${bestTf.label} returns (${sign(bestTf.aV)} vs ${sign(bestTf.bV)})`);
    } else {
      const connector = parts.length === 0
        ? ''
        : yieldWinner && yieldWinner !== winner.t
          ? ' but '
          : ' and ';
      parts.push(
        `${connector}${winner.t} outperformed over ${bestTf.label} (${sign(winner.v)} vs ${sign(winner.l)})`,
      );
    }
  }

  if (parts.length === 0) return null;
  // Capitalise first letter of the combined sentence.
  const raw = parts.join('').trim();
  return raw.charAt(0).toUpperCase() + raw.slice(1) + '.';
}

function SummaryBanner({ text }) {
  if (!text) return null;
  return (
    <div
      className="rounded-xl border border-primary/20 bg-gradient-to-r from-sky-50 to-violet-50 dark:from-sky-950/30 dark:to-violet-950/30 p-3 sm:p-4"
      data-testid="compare-summary-banner"
    >
      <div className="flex items-start gap-2">
        <span className="mt-0.5 text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground shrink-0 hidden sm:inline">
          Takeaway
        </span>
        <p className="text-sm sm:text-base leading-snug font-medium text-foreground">
          {text}
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Match Strength — out of 5 criteria:
//   1. Same (or both-blank) sector
//   2. Same dividend yield bucket (0-3 / 3-5 / 5-8 / 8-12 / 12+)
//   3. Same payout frequency
//   4. Same risk flags (is_leveraged AND uses_futures AND is_adr)
//   5. 1-year performance within 10 percentage points
// Produces a 1-5 score. Score=1 means we have so little in common the
// comparison is apples-to-oranges ("Mismatched"); 5 means near-identical
// profile ("Very Strong"). Helps users realise when the comparison is
// actually meaningful.
// ---------------------------------------------------------------------------
function yieldBucket(y) {
  if (typeof y !== 'number' || y <= 0) return null;
  if (y < 3) return '0-3';
  if (y < 5) return '3-5';
  if (y < 8) return '5-8';
  if (y < 12) return '8-12';
  return '12+';
}

function computeMatch(aDetail, bDetail) {
  if (!aDetail || !bDetail) return null;
  const criteria = [];

  // 1. Sector — treat two ETFs (both blank) as matching.
  const aSec = (aDetail.sector || '').trim();
  const bSec = (bDetail.sector || '').trim();
  const sectorMet = aSec === bSec;
  criteria.push({
    label: 'Sector',
    met: sectorMet,
    detail: sectorMet
      ? aSec ? `Both ${aSec}` : 'Both ETFs / uncategorised'
      : `${aSec || 'Uncategorised'} vs ${bSec || 'Uncategorised'}`,
  });

  // 2. Yield band
  const aBucket = yieldBucket(aDetail.dividend_yield);
  const bBucket = yieldBucket(bDetail.dividend_yield);
  const yieldMet = Boolean(aBucket && bBucket && aBucket === bBucket);
  criteria.push({
    label: 'Yield band',
    met: yieldMet,
    detail: !aBucket || !bBucket
      ? 'Missing yield data'
      : yieldMet
        ? `Both in ${aBucket}% band`
        : `${aBucket}% vs ${bBucket}% — different bands`,
  });

  // 3. Frequency
  const aFreq = (aDetail.frequency || '').trim();
  const bFreq = (bDetail.frequency || '').trim();
  const freqMet = Boolean(aFreq && bFreq && aFreq === bFreq);
  criteria.push({
    label: 'Payout frequency',
    met: freqMet,
    detail: !aFreq || !bFreq
      ? 'Missing frequency data'
      : freqMet
        ? `Both ${aFreq}`
        : `${aFreq} vs ${bFreq}`,
  });

  // 4. Risk flags
  const levMatch = Boolean(aDetail.is_leveraged) === Boolean(bDetail.is_leveraged);
  const futMatch = Boolean(aDetail.uses_futures) === Boolean(bDetail.uses_futures);
  const adrMatch = Boolean(aDetail.is_adr) === Boolean(bDetail.is_adr);
  const bdcMatch = Boolean(aDetail.is_bdc) === Boolean(bDetail.is_bdc);
  const cefMatch = Boolean(aDetail.is_cef) === Boolean(bDetail.is_cef);
  const riskMet = levMatch && futMatch && adrMatch && bdcMatch && cefMatch;
  let riskDetail;
  if (riskMet) {
    const tags = [];
    if (aDetail.is_leveraged) tags.push('leveraged');
    if (aDetail.uses_futures) tags.push('futures-based');
    if (aDetail.is_adr) tags.push('ADRs');
    if (aDetail.is_bdc) tags.push('BDCs');
    if (aDetail.is_cef) tags.push('CEFs');
    if (tags.length) {
      riskDetail = `Both flagged ${tags.join(' + ')}`;
    } else {
      riskDetail = 'Both non-leveraged, no futures, domestic';
    }
  } else {
    // Surface the specific axis (or axes) that differ so users see exactly
    // why the risk profiles don't line up.
    const diffs = [];
    if (!levMatch) {
      diffs.push(aDetail.is_leveraged ? 'A is leveraged, B is not' : 'B is leveraged, A is not');
    }
    if (!futMatch) {
      diffs.push(aDetail.uses_futures ? 'A is futures-based, B is not' : 'B is futures-based, A is not');
    }
    if (!adrMatch) {
      diffs.push(aDetail.is_adr ? 'A is an ADR, B is U.S.-domiciled' : 'B is an ADR, A is U.S.-domiciled');
    }
    if (!bdcMatch) {
      diffs.push(aDetail.is_bdc ? 'A is a BDC, B is not' : 'B is a BDC, A is not');
    }
    if (!cefMatch) {
      diffs.push(aDetail.is_cef ? 'A is a CEF, B is not' : 'B is a CEF, A is not');
    }
    riskDetail = diffs.join('; ') || 'Different risk profiles';
  }
  criteria.push({ label: 'Risk profile', met: riskMet, detail: riskDetail });

  // 5. 1Y performance alignment (within 10 pp)
  const aP = aDetail.perf_1y;
  const bP = bDetail.perf_1y;
  let perfMet = false;
  let perfDetail;
  if (typeof aP === 'number' && typeof bP === 'number') {
    const diff = Math.abs(aP - bP);
    perfMet = diff <= 10;
    perfDetail = perfMet
      ? `1Y returns within ${diff.toFixed(1)}pp`
      : `1Y returns differ by ${diff.toFixed(1)}pp`;
  } else {
    perfDetail = 'Missing 1Y performance data';
  }
  criteria.push({ label: '1Y performance', met: perfMet, detail: perfDetail });

  const score = Math.max(1, Math.min(5, criteria.filter((c) => c.met).length));
  return { score, criteria };
}

const MATCH_LABELS = [
  null,
  'Mismatched',
  'Slightly Matched',
  'Fair Match',
  'Strong Match',
  'Very Strong Match',
];

// Fill colors for lit boxes. Warmer at low scores → green at high scores.
const MATCH_FILL = [
  '',
  'bg-red-500',       // 1
  'bg-orange-500',    // 2
  'bg-amber-500',     // 3
  'bg-lime-500',      // 4
  'bg-emerald-500',   // 5
];

function MatchMeter({ match }) {
  const [open, setOpen] = useState(false);
  if (!match) return null;
  const { score, criteria } = match;
  const label = MATCH_LABELS[score];
  const fill = MATCH_FILL[score];
  return (
    <div
      className="flex flex-col items-center gap-1.5 py-2"
      data-testid={`compare-match-meter-${score}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-muted/50 transition-colors cursor-pointer"
        aria-expanded={open}
        aria-label={`Match strength ${score} of 5. Click for breakdown.`}
        data-testid="compare-match-meter-toggle"
        title="Click to see what scored and what didn't"
      >
        <div className="flex items-end gap-1">
          {[1, 2, 3, 4, 5].map((i) => (
            <span
              key={i}
              className={`w-2 h-6 rounded-[2px] border ${
                i <= score
                  ? `${fill} border-transparent`
                  : 'bg-muted/40 border-border'
              }`}
            />
          ))}
        </div>
        <span className="text-xs sm:text-sm font-semibold text-muted-foreground">{label}</span>
        <svg
          className={`w-3 h-3 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-[10px] sm:text-[11px] text-muted-foreground/80 hover:text-primary underline decoration-dotted underline-offset-2 cursor-pointer"
          data-testid="compare-match-hint"
        >
          Click to see what scored and what didn&apos;t
        </button>
      )}
      {open && (
        <div
          className="w-full sm:min-w-[260px] sm:max-w-[320px] rounded-lg border bg-popover text-popover-foreground shadow-sm p-3 space-y-1.5"
          data-testid="compare-match-breakdown"
        >
          {criteria.map((c) => (
            <div
              key={c.label}
              className="flex items-start gap-2 text-xs sm:text-[13px]"
              data-testid={`compare-match-row-${c.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              <span className={`mt-0.5 shrink-0 font-bold ${c.met ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-500 dark:text-rose-400'}`}>
                {c.met ? '✓' : '✗'}
              </span>
              <div className="flex-1 min-w-0">
                <span className="font-semibold">{c.label}</span>
                <span className="text-muted-foreground"> — {c.detail}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
export default function CompareModal({ open, onClose, isDark, initialTickerA, initialTickerB, watchlist, onToggleWatchlist, user, onLoginRequired, onOpenStockModal }) {
  // slot = { ticker, detail, growth, yieldHist, loading, error }
  const [slotA, setSlotA] = useState(null);
  const [slotB, setSlotB] = useState(null);
  const [history, setHistory] = useState([]);
  const [exporting, setExporting] = useState(false);
  // Round 89e — pinned pairs. Server-backed for logged-in users; this
  // modal only writes to its own modal copy of the set (the Watchlist
  // tab listens for the `compare-pins-changed` window event below to
  // refresh its own mini-card grid).
  const [pinnedPairs, setPinnedPairs] = useState([]);
  const [togglingPin, setTogglingPin] = useState(false);
  // Round 89f — inline note editor for the currently-pinned pair.
  // Closed by default; opening copies the saved note into the draft
  // textarea so Esc/cancel cleanly discards edits.
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const captureRef = useRef(null);

  // Whether the currently-locked pair {A,B} is already pinned.
  const currentPinKey = (() => {
    if (!slotA?.ticker || !slotB?.ticker) return null;
    const [a, b] = [slotA.ticker, slotB.ticker].sort();
    return `${a}__${b}`;
  })();
  const currentPinRecord = currentPinKey
    ? pinnedPairs.find((p) => {
        const [a, b] = [p.a, p.b].sort();
        return `${a}__${b}` === currentPinKey;
      })
    : null;
  const isCurrentPinned = !!currentPinRecord;
  const currentPinNote = currentPinRecord?.note || null;

  // Load pinned pairs once on open for logged-in users so the Pin
  // button can render the correct "Pinned ✓" / "Pin pair" state.
  useEffect(() => {
    if (!open || !user) return;
    let cancelled = false;
    axios
      .get(`${API}/compare-pins`, { withCredentials: true })
      .then((r) => { if (!cancelled) setPinnedPairs(r.data || []); })
      .catch(() => { if (!cancelled) setPinnedPairs([]); });
    return () => { cancelled = true; };
  }, [open, user]);

  const fetchSlot = useCallback(async (ticker) => {
    const t = ticker.toUpperCase().trim();
    // Kick off detail first so the ticker+name can render fast.
    try {
      const detailPromise = axios.get(`${API}/stocks/${t}`);
      const growthPromise = axios.get(`${API}/stocks/${t}/dividend-growth`).catch(() => ({ data: null }));
      const yieldPromise = axios.get(`${API}/stocks/${t}/yield-history`).catch(() => ({ data: null }));
      // Round 96b (Feb 2026) — Pull triangle data too so we can render
      // Revenue + EPS comparison charts for non-dividend pairs (where
      // the Dividend Growth + Yield charts are useless).
      const trianglePromise = axios.get(`${API}/stocks/${t}/triangle`).catch(() => ({ data: null }));
      // Round 99a (Jul 2026) — Alt-metrics (FFO/NII/DCF/NAV) for
      // REITs/BDCs/MLPs/CEFs. Returns {asset_class: null} for common
      // stocks so the render can transparently fall back to EPS.
      const altMetricsPromise = axios.get(`${API}/stocks/${t}/alt-metrics`).catch(() => ({ data: null }));
      // Round 96b (Feb 2026) — Pull 1Y daily price history so the
      // Price Performance chart can render a YTD bar. The detail
      // endpoint exposes 1M / 6M / 1Y / 5Y CAGR but no YTD field.
      // Computing it client-side from the daily series keeps the
      // backend untouched while giving us the single most-asked
      // timeframe for retail investors.
      const pricesPromise = axios
        .get(`${API}/stocks/${t}/price-history`, { params: { range: '1y' } })
        .catch(() => ({ data: null }));
      const [d, g, y, tri, alt, ph] = await Promise.all([
        detailPromise, growthPromise, yieldPromise, trianglePromise, altMetricsPromise, pricesPromise,
      ]);

      // Derive YTD return from the daily price series: first close on
      // or after Jan 1 of the current year vs the most recent close.
      let ytdReturn = null;
      try {
        const prices = ph?.data?.prices || [];
        if (prices.length > 0) {
          const yearStart = `${new Date().getUTCFullYear()}-01-01`;
          const firstOfYear = prices.find((p) => p.date >= yearStart);
          const latest = prices[prices.length - 1];
          if (firstOfYear && latest && firstOfYear.close > 0) {
            ytdReturn = ((latest.close - firstOfYear.close) / firstOfYear.close) * 100;
          }
        }
      } catch (e) {
        ytdReturn = null;
      }

      return {
        detail: d.data ? { ...d.data, _ytd_return: ytdReturn } : null,
        growth: g.data,
        yieldHist: y.data,
        triangle: tri.data,
        altMetrics: alt.data,
      };
    } catch (err) {
      return { detail: null, growth: null, yieldHist: null, triangle: null, altMetrics: null, error: err?.message || 'Failed to load' };
    }
  }, []);

  const lockSlot = useCallback(
    async (which, ticker) => {
      const setter = which === 'A' ? setSlotA : setSlotB;
      setter({ ticker: ticker.toUpperCase(), loading: true });
      const data = await fetchSlot(ticker);
      setter({ ticker: ticker.toUpperCase(), loading: false, ...data });
    },
    [fetchSlot],
  );

  const unlockSlot = useCallback((which) => {
    if (which === 'A') setSlotA(null);
    else setSlotB(null);
  }, []);

  // Phase C (May 2026) — pin/re-pin BOTH slots to the latest live data.
  // Re-runs the same parallel fetch the modal already uses on lock so
  // the user can re-anchor the comparison to current prices without
  // unlocking + relocking either side.
  const [pinnedAt, setPinnedAt] = useState(null);
  const [refreshingPrices, setRefreshingPrices] = useState(false);
  const handleRefreshPrices = useCallback(async () => {
    if (refreshingPrices) return;
    if (!slotA?.ticker && !slotB?.ticker) return;
    setRefreshingPrices(true);
    try {
      const tasks = [];
      if (slotA?.ticker) tasks.push(fetchSlot(slotA.ticker).then((d) => ['A', d]));
      if (slotB?.ticker) tasks.push(fetchSlot(slotB.ticker).then((d) => ['B', d]));
      const results = await Promise.all(tasks);
      results.forEach(([which, data]) => {
        const setter = which === 'A' ? setSlotA : setSlotB;
        setter((prev) => (prev ? { ...prev, ...data, loading: false } : prev));
      });
      setPinnedAt(new Date().toISOString());
    } finally {
      setRefreshingPrices(false);
    }
  }, [slotA?.ticker, slotB?.ticker, fetchSlot, refreshingPrices]);

  // Export the comparison card as a PNG. Uses html-to-image; filename
  // includes both tickers so downloads stay organised. Runs on the currently
  // visible card so charts + tables capture exactly as rendered.
  const handleExportPng = useCallback(async () => {
    const node = captureRef.current;
    if (!node) return;
    setExporting(true);
    try {
      const aT = slotA?.ticker || 'A';
      const bT = slotB?.ticker || 'B';
      // Derive a readable bg color so transparent dark-mode captures look right.
      const bg = getComputedStyle(document.body).backgroundColor || (isDark ? '#0a0a0a' : '#ffffff');
      const dataUrl = await toPng(node, {
        cacheBust: true,
        backgroundColor: bg,
        pixelRatio: 2, // crisp on Retina / 4K screens
      });
      const link = document.createElement('a');
      link.download = `dividrip-compare-${aT}-vs-${bT}.png`;
      link.href = dataUrl;
      link.click();
      toast.success('Comparison image saved');
    } catch (err) {
      toast.error('Could not export image');
      // Fail silently in production; helpful signal in dev tools.
      // eslint-disable-next-line no-console
      console.warn('CompareModal export failed:', err);
    } finally {
      setExporting(false);
    }
  }, [slotA?.ticker, slotB?.ticker, isDark]);

  // Build the shareable caption once — reused by Share on X and Copy.
  // Converts plain tickers (e.g. "JEPI") to cashtags ($JEPI) for discoverability.
  const buildShareCaption = useCallback(() => {
    const aT = slotA?.ticker;
    const bT = slotB?.ticker;
    if (!aT || !bT) return null;
    const takeaway = buildSummary(slotA, slotB, slotA?.detail, slotB?.detail);
    const lead = takeaway
      ? `Just compared $${aT} vs $${bT} on DiviDrip — ${takeaway
          .replace(new RegExp(`\\b${aT}\\b`, 'g'), `$${aT}`)
          .replace(new RegExp(`\\b${bT}\\b`, 'g'), `$${bT}`)}`
      : `Just compared $${aT} vs $${bT} on DiviDrip.`;
    const url = typeof window !== 'undefined' ? window.location.origin : '';
    return `${lead}\n\n📊 ${url}`;
  }, [slotA, slotB]);

  // Open a pre-composed X / Twitter post with the takeaway line + $TICKER
  // cashtags + the app URL. Pure intent URL — no SDK, no auth. Users can
  // drag-drop the exported PNG into the tweet for the full visual.
  const handleShareToX = useCallback(() => {
    const text = buildShareCaption();
    if (!text) return;
    const intent = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    window.open(intent, '_blank', 'noopener,noreferrer');
  }, [buildShareCaption]);

  // Copy the share caption to clipboard for pasting into Reddit, Discord,
  // text messages, or anywhere that's not X. Uses the async Clipboard API
  // with a legacy execCommand fallback for older browsers.
  const handleCopyCaption = useCallback(async () => {
    const text = buildShareCaption();
    if (!text) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      toast.success('Caption copied — paste anywhere');
    } catch {
      toast.error('Could not copy — try selecting manually');
    }
  }, [buildShareCaption]);

  // Round 89e — Toggle pin/unpin for the currently locked pair.
  // Both slots must be locked. Optimistically updates local state so
  // the icon flips instantly; rolls back on server error. Dispatches
  // `compare-pins-changed` so the Watchlist tab's pinned-pairs section
  // refreshes without a route change.
  const handleTogglePin = useCallback(async () => {
    if (!slotA?.ticker || !slotB?.ticker || togglingPin) return;
    if (!user) {
      // Round 97l (Feb 2026) — Stash the pair the user was trying to
      // pin BEFORE we surface the login modal. After auth completes
      // the Dashboard re-opens CompareModal with this exact pair and
      // a `pending_compare_pin_autofire` flag — the modal\'s
      // post-login useEffect then auto-fires the pin once both slots
      // are locked, so the user lands on the Watchlist with the
      // pinned pair instead of an empty compare screen. Also store
      // the redirect target so the post-login flow returns the user
      // to the Watchlist where the pinned-pairs row lives.
      try {
        sessionStorage.setItem(
          'pending_compare_pin',
          JSON.stringify({
            a: slotA.ticker,
            b: slotB.ticker,
            ts: Date.now(),
          })
        );
        sessionStorage.setItem('post_login_redirect', `${window.location.origin}/?view=watchlist`);
      } catch (e) { /* ignore quota / disabled storage */ }
      if (typeof onLoginRequired === 'function') onLoginRequired();
      return;
    }
    const a = slotA.ticker;
    const b = slotB.ticker;
    const [na, nb] = [a, b].sort();
    setTogglingPin(true);
    try {
      if (isCurrentPinned) {
        await axios.delete(`${API}/compare-pins`, {
          params: { a: na, b: nb },
          withCredentials: true,
        });
        setPinnedPairs((prev) => prev.filter((p) => {
          const [pa, pb] = [p.a, p.b].sort();
          return !(pa === na && pb === nb);
        }));
        toast.success(`Unpinned ${a} vs ${b}`);
      } else {
        await axios.post(
          `${API}/compare-pins`,
          { a: na, b: nb },
          { withCredentials: true },
        );
        setPinnedPairs((prev) => [
          { a: na, b: nb, pinned_at: new Date().toISOString(), note: null },
          ...prev.filter((p) => {
            const [pa, pb] = [p.a, p.b].sort();
            return !(pa === na && pb === nb);
          }),
        ]);
        toast.success(`Pinned ${a} vs ${b}`, {
          description: 'Find it on the Watchlist tab under "Pinned Comparisons".',
          duration: 4000,
        });
      }
      window.dispatchEvent(new Event('compare-pins-changed'));
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not update pin — try again';
      toast.error(msg);
    } finally {
      setTogglingPin(false);
    }
  }, [slotA, slotB, isCurrentPinned, togglingPin, user, onLoginRequired]);

  // Round 97l (Feb 2026) — Resume the pin after the user logs in.
  // We persist the pending {a,b} pair in sessionStorage when Pin
  // Compare is clicked logged-out (see handleTogglePin above). On
  // mount AND every slot/state change we re-check: if the user is
  // logged in, both slots are locked to the stashed pair, and we
  // aren\'t mid-toggle, fire the pin. Synchronous off sessionStorage
  // so there\'s no event-dispatch race with the modal re-opening
  // after auth.
  //
  // Placement note: this hook MUST live BELOW the
  // `handleTogglePin = useCallback(...)` declaration above —
  // referencing it earlier throws "Cannot access 'handleTogglePin'
  // before initialization" (temporal dead zone) and blanks the app.
  useEffect(() => {
    if (!open || !user) return;
    if (!slotA?.ticker || !slotB?.ticker) return;
    if (togglingPin) return;
    let pending = null;
    try {
      const raw = sessionStorage.getItem('pending_compare_pin');
      if (raw) pending = JSON.parse(raw);
    } catch (e) { /* noop */ }
    if (!pending?.a || !pending?.b) return;
    if (slotA.ticker !== pending.a || slotB.ticker !== pending.b) return;
    if (Date.now() - (pending.ts || 0) > 10 * 60 * 1000) {
      try { sessionStorage.removeItem('pending_compare_pin'); } catch { /* noop */ }
      return;
    }
    try { sessionStorage.removeItem('pending_compare_pin'); } catch { /* noop */ }
    if (isCurrentPinned) {
      toast.success(`${pending.a} vs ${pending.b} is already pinned.`, {
        description: 'Find it on the Watchlist tab under "Pinned Comparisons".',
        duration: 4000,
      });
      return;
    }
    handleTogglePin();
  }, [open, user, slotA?.ticker, slotB?.ticker, togglingPin, isCurrentPinned, handleTogglePin]);

  // Round 89f — Save / clear the memo on the currently pinned pair.
  // Uses PATCH so `pinned_at` doesn't shift (otherwise editing a note
  // would jump the card to the top of the Watchlist mini-card row).
  const handleSaveNote = useCallback(async () => {
    if (!slotA?.ticker || !slotB?.ticker || savingNote) return;
    const a = slotA.ticker;
    const b = slotB.ticker;
    const [na, nb] = [a, b].sort();
    const trimmed = noteDraft.trim().slice(0, 140);
    setSavingNote(true);
    try {
      const { data } = await axios.patch(
        `${API}/compare-pins/note`,
        { a: na, b: nb, note: trimmed || null },
        { withCredentials: true },
      );
      // Reflect locally so the header chip updates instantly.
      setPinnedPairs((prev) => prev.map((p) => {
        const [pa, pb] = [p.a, p.b].sort();
        if (pa === na && pb === nb) return { ...p, note: data?.note ?? null };
        return p;
      }));
      window.dispatchEvent(new Event('compare-pins-changed'));
      setNoteOpen(false);
      toast.success(trimmed ? 'Note saved' : 'Note cleared');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not save note';
      toast.error(msg);
    } finally {
      setSavingNote(false);
    }
  }, [slotA, slotB, savingNote, noteDraft]);


  // Reset when closed so re-opening starts fresh.
  useEffect(() => {
    if (!open) {
      setSlotA(null);
      setSlotB(null);
    } else {
      // Refresh the recently-compared list each time the modal opens.
      // Logged in → fetch from MongoDB (cross-device).
      // Logged out → empty (no history surfaced for anonymous users).
      if (user) {
        let cancelled = false;
        loadHistoryRemote().then((rows) => {
          if (!cancelled) setHistory(rows);
        });
        return () => { cancelled = true; };
      }
      setHistory([]);
    }
  }, [open, user]);

  // Whenever both slots are filled (or slot A is filled without B), persist
  // the pair so users can jump back into the same face-off later.
  // Only persists when the user is logged in (per product spec —
  // anonymous browsing should leave no trace). Backend write +
  // optimistic local-state update so the new pill appears instantly.
  useEffect(() => {
    if (!open) return;
    if (!user) return;
    const aT = slotA?.ticker;
    const bT = slotB?.ticker;
    if (!aT) return;
    let cancelled = false;
    (async () => {
      await saveHistoryPairRemote(aT, bT);
      if (cancelled) return;
      const rows = await loadHistoryRemote();
      if (!cancelled) setHistory(rows);
    })();
    return () => { cancelled = true; };
  }, [open, slotA?.ticker, slotB?.ticker, user]);

  // Auto-lock slot A (and optionally B) if caller passed initial
  // tickers (e.g. launched from StockModal's "Compare" shortcut, or
  // when restoring a snapshot via "← Back to Compare").
  useEffect(() => {
    if (open && initialTickerA) {
      lockSlot('A', initialTickerA);
    }
    if (open && initialTickerB) {
      lockSlot('B', initialTickerB);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialTickerA, initialTickerB]);

  const a = slotA;
  const b = slotB;
  const aDetail = a?.detail;
  const bDetail = b?.detail;

  // Signal badges
  const aSignal = computeSignal(aDetail, a?.growth);
  const bSignal = computeSignal(bDetail, b?.growth);

  // Numeric values for winner comparisons
  const aYield = aDetail?.dividend_yield ?? null;
  const bYield = bDetail?.dividend_yield ?? null;
  const aRate = aDetail?.dividend_rate ?? null;
  const bRate = bDetail?.dividend_rate ?? null;
  const aPayout = aDetail?.payout_ratio ?? null;
  const bPayout = bDetail?.payout_ratio ?? null;
  const aAvg = a?.yieldHist?.avg_yield_3yr ?? null;
  const bAvg = b?.yieldHist?.avg_yield_3yr ?? null;
  const aPrice = aDetail?.price ?? null;
  const bPrice = bDetail?.price ?? null;
  const aP1 = aDetail?.perf_1m ?? null;
  const bP1 = bDetail?.perf_1m ?? null;
  const aP6 = aDetail?.perf_6m ?? null;
  const bP6 = bDetail?.perf_6m ?? null;
  const aP1y = aDetail?.perf_1y ?? null;
  const bP1y = bDetail?.perf_1y ?? null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="max-w-5xl sm:max-w-5xl max-h-[92vh] overflow-y-auto p-0 gap-0"
        data-testid="compare-modal"
        // Match StockModal behaviour: only the X button (and the Esc
        // key, kept for keyboard accessibility) close the dialog. A
        // misfired drag/swipe outside the modal won't kill the user's
        // in-progress edits — see Apr-2026 user report on DRIP Calc
        // monthly-investment input.
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="px-4 sm:px-6 pt-5 pb-3 border-b">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <DialogTitle className="text-lg sm:text-xl font-bold flex items-center gap-2">
                <GitCompare className="w-5 h-5 text-fuchsia-500 dark:text-fuchsia-400 shrink-0" />
                <span>Side-by-Side Comparison</span>
              </DialogTitle>
              <DialogDescription className="text-xs">
                Pick any two stocks or ETFs — dividend, non-dividend, REIT, BDC, CEF, MLP — to compare yield, growth, and price performance.
              </DialogDescription>
            </div>
            {/* Phase C (May 2026) — re-fetch BOTH slots so the snapshot
                used by this comparison anchors to current prices. Hidden
                until at least one slot is locked. */}
            {(slotA?.ticker || slotB?.ticker) && (
              <div className="mt-1 mr-8 shrink-0">
                <RepinPriceButton
                  onClick={handleRefreshPrices}
                  pinnedAt={pinnedAt}
                  busy={refreshingPrices}
                  testId="compare-refresh-price"
                />
              </div>
            )}
          </div>
        </DialogHeader>

        {/* Inner scrollable body. Extra `pb-24` on mobile gives the
            user enough room to scroll past the bottom of the content
            and not have the "Made with Emergent" floating badge cover
            it. Desktop reverts to the original `pb-6` since the badge
            doesn't overlap the modal at wide widths. */}
        <div className="px-3 sm:px-6 pt-4 pb-24 sm:pb-6 space-y-5">
          {/* Slots row — on desktop: [A narrow] [meter centered] [B narrow].
              On mobile: stacked vertically with meter after both slots
              (via order classes) so the user still sees the slots side-by-
              side relationship first. Widths capped at w-60 on desktop
              since a ticker + yield % never needs more than ~180px. */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="w-full sm:w-60 sm:shrink-0 flex sm:order-1">
              {a ? (
                <LockedCard
                  slot="A"
                  ticker={a.ticker}
                  name={aDetail?.name}
                  loading={a.loading}
                  onUnlock={() => unlockSlot('A')}
                  watchlist={watchlist}
                  onToggleWatchlist={onToggleWatchlist}
                  user={user}
                  onLoginRequired={onLoginRequired}
                  onOpenStockModal={
                    onOpenStockModal
                      ? (tk) => onOpenStockModal(tk, { a: a?.ticker, b: b?.ticker })
                      : undefined
                  }
                />
              ) : (
                <SearchBox
                  slot="A"
                  onLock={(t) => lockSlot('A', t)}
                  // P158·15 (Feb 2026) — Two-way MMF lock. When B is
                  // already locked, force A's search to either only
                  // find MMFs (if B is one) or hide MMFs (if B is a
                  // regular stock — MMFs would compare to blank rows).
                  mmfMode={bDetail ? (bDetail.is_money_market_fund ? 'only' : 'hide') : undefined}
                />
              )}
            </div>
            <div className="w-full sm:w-60 sm:shrink-0 flex sm:order-3">
              {b ? (
                <LockedCard
                  slot="B"
                  ticker={b.ticker}
                  name={bDetail?.name}
                  loading={b.loading}
                  onUnlock={() => unlockSlot('B')}
                  watchlist={watchlist}
                  onToggleWatchlist={onToggleWatchlist}
                  user={user}
                  onLoginRequired={onLoginRequired}
                  onOpenStockModal={
                    onOpenStockModal
                      ? (tk) => onOpenStockModal(tk, { a: a?.ticker, b: b?.ticker })
                      : undefined
                  }
                />
              ) : (
                <SearchBox
                  slot="B"
                  onLock={(t) => lockSlot('B', t)}
                  mmfMode={aDetail ? (aDetail.is_money_market_fund ? 'only' : 'hide') : undefined}
                />
              )}
            </div>
            {aDetail && bDetail && !(aDetail.is_money_market_fund && bDetail.is_money_market_fund) && (
              <div className="flex-1 flex justify-center sm:order-2">
                <MatchMeter match={computeMatch(aDetail, bDetail)} />
              </div>
            )}
          </div>

          {/* P158·16 (Feb 2026) — When both slots are MMFs, render the
              MMF-specific compare card and skip the generic table,
              MatchMeter, SummaryBanner, and yield-history chart —
              MMFs have no revenue / EPS / DGR / triangle metrics, so
              the standard comparison collapses to em-dashes and the
              MatchMeter mis-labels them as ETFs. */}
          {aDetail?.is_money_market_fund && bDetail?.is_money_market_fund && (
            <MmfCompareCard aDetail={aDetail} bDetail={bDetail} />
          )}

          {/* Capturable region — table + charts. Slots & the empty state
              are excluded; the PNG should just contain the comparison. */}
          <div ref={captureRef} className={`space-y-5 ${aDetail?.is_money_market_fund && bDetail?.is_money_market_fund ? 'hidden' : ''}`}>
            {/* Plain-English takeaway — rendered inside the capture region
                so it ships with PNG exports (extra-viral for sharing). */}
            <SummaryBanner text={buildSummary(a, b, aDetail, bDetail)} />

            {/* Comparison — shows once at least one slot has data */}
            {(aDetail || bDetail) && (
              <div className="rounded-xl border bg-card overflow-hidden" data-testid="compare-table">
                <Row label="Metric" a={<span className={SLOT_STYLES.A.textLocked + ' font-bold'}>{a?.ticker || '—'}</span>} b={<span className={SLOT_STYLES.B.textLocked + ' font-bold'}>{b?.ticker || '—'}</span>} strong />
                <Row
                  label="Signal"
                  a={<SignalPill signal={aSignal} />}
                  b={<SignalPill signal={bSignal} />}
                />

                <SectionTitle>Dividend Metrics</SectionTitle>
                <Row
                  label="Div Yield"
                  a={fmtPct(aYield)}
                  b={fmtPct(bYield)}
                  winnerSide={winner(aYield, bYield, 'higher')}
                />
                <Row
                  label="Ann. Dividend"
                  a={fmtUsd(aRate)}
                  b={fmtUsd(bRate)}
                  winnerSide={winner(aRate, bRate, 'higher')}
                />
                <Row label="Payout Period" a={aDetail?.frequency || null} b={bDetail?.frequency || null} />
                <Row
                  label="Est. Payout Ratio"
                  a={fmtPct(aPayout || null)}
                  b={fmtPct(bPayout || null)}
                  winnerSide={aPayout && bPayout ? winner(aPayout, bPayout, 'lower') : null}
                />
                <Row
                  label="3yr Avg Yield"
                  a={fmtPct(aAvg || null)}
                  b={fmtPct(bAvg || null)}
                  winnerSide={aAvg && bAvg ? winner(aAvg, bAvg, 'higher') : null}
                />

                <SectionTitle>Price & Performance</SectionTitle>
                <Row label="Price" a={fmtUsd(aPrice)} b={fmtUsd(bPrice)} />
                <Row
                  label="1 Month"
                  a={fmtPct(aP1, true)}
                  b={fmtPct(bP1, true)}
                  winnerSide={winner(aP1, bP1, 'higher')}
                />
                <Row
                  label="6 Months"
                  a={fmtPct(aP6, true)}
                  b={fmtPct(bP6, true)}
                  winnerSide={winner(aP6, bP6, 'higher')}
                />
                <Row
                  label="1 Year"
                  a={fmtPct(aP1y, true)}
                  b={fmtPct(bP1y, true)}
                  winnerSide={winner(aP1y, bP1y, 'higher')}
                />
              </div>
            )}

            {/* Round 99a (Jul 2026) — Asset-class informational banner.
                REITs, BDCs, and MLPs measure earnings power differently
                from common stocks. FFO / NII / DCF are now computed by
                `/api/stocks/{ticker}/alt-metrics` and swapped in below,
                so this banner is purely informational (tells the user
                what metric is being used) — no longer a "this is on the
                roadmap" caveat. */}
            {(() => {
              const aAltClass = a?.altMetrics?.asset_class || null;
              const bAltClass = b?.altMetrics?.asset_class || null;
              if (!aAltClass && !bAltClass) return null;
              const parts = [];
              if (aAltClass) parts.push(`${a?.ticker}: ${aAltClass} → ${a?.altMetrics?.primary_metric || ''}`);
              if (bAltClass) parts.push(`${b?.ticker}: ${bAltClass} → ${b?.altMetrics?.primary_metric || ''}`);
              return (
                <div
                  className="mx-2 sm:mx-6 rounded-md border border-violet-300/50 bg-violet-50/60 dark:border-violet-700/40 dark:bg-violet-950/30 px-3 py-2 text-[11px] flex items-start gap-2"
                  data-testid="compare-asset-class-banner"
                >
                  <span className="text-violet-600 dark:text-violet-400 mt-0.5">◆</span>
                  <div className="text-violet-900 dark:text-violet-200 leading-tight">
                    <span className="font-semibold">Alt-metrics active.</span>{' '}
                    {parts.join(' · ')}. The EPS chart below is replaced with the class-appropriate cash-earnings metric.
                  </div>
                </div>
              );
            })()}

            {/* Sep 2026 — Reckoner CLO nesting-doll banner. When either
                slot is one of the six Reckoner funds, explain that the
                wrappers hold the core fund and offer one-click pairs. */}
            {(() => {
              const SUITES = {
                AAA: { core: 'RAAA', annual: 'RAAY', reinvest: 'RAAR' },
                'BBB-B': { core: 'RCLO', annual: 'RCLY', reinvest: 'RCLR' },
              };
              const familyOf = (t) => Object.keys(SUITES).find((k) => Object.values(SUITES[k]).includes(t));
              const fam = familyOf(a?.ticker) || familyOf(b?.ticker);
              if (!fam) return null;
              const su = SUITES[fam];
              const pairs = [
                [su.reinvest, su.core, 'Reinvesting vs core'],
                [su.annual, su.core, 'Annual vs core'],
                [su.reinvest, su.annual, 'Reinvesting vs Annual'],
              ];
              const cur = `${a?.ticker || ''}|${b?.ticker || ''}`;
              return (
                <div
                  className="mx-2 sm:mx-6 rounded-md border border-sky-300/60 bg-sky-50/70 dark:border-sky-700/40 dark:bg-sky-950/30 px-3 py-2 text-[11px]"
                  data-testid="compare-nesting-doll-banner"
                >
                  <div className="flex items-start gap-2 text-sky-900 dark:text-sky-200 leading-tight">
                    <span className="text-sky-600 dark:text-sky-400 mt-0.5">🪆</span>
                    <div>
                      <span className="font-semibold">Nesting-doll funds — same portfolio, different payout clock.</span>{' '}
                      {su.reinvest} holds ~99% {su.annual}, which holds ~99% {su.core}; only {su.core} owns the {fam} CLO tranches.
                      Dividend and yield charts will look lopsided by design — judge the wrappers on price (NAV) growth tracking the core.
                    </div>
                  </div>
                  <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                    {pairs.map(([x, y, label]) => {
                      const active = cur === `${x}|${y}` || cur === `${y}|${x}`;
                      return (
                        <button
                          key={label}
                          type="button"
                          disabled={active}
                          onClick={() => { lockSlot('A', x); lockSlot('B', y); }}
                          className={`px-2 py-0.5 rounded border font-mono text-[10px] font-bold transition-colors ${active ? 'bg-sky-600 text-white border-sky-600 cursor-default' : 'bg-white dark:bg-slate-900 text-sky-700 dark:text-sky-300 border-sky-300 dark:border-sky-700 hover:bg-sky-100 dark:hover:bg-sky-900/50'}`}
                          data-testid={`compare-nesting-pair-${x}-${y}`}
                          title={label}
                        >
                          {x} vs {y}
                        </button>
                      );
                    })}
                    <Link to="/learn/clo-etfs-nesting-doll" className="ml-auto text-[10px] font-semibold text-sky-700 dark:text-sky-300 hover:underline" data-testid="compare-nesting-learn-link">
                      How the nesting doll works →
                    </Link>
                  </div>
                </div>
              );
            })()}

            {/* Charts — need at least one loaded slot with data.
                Wrapped in a horizontally-padded container so the floating
                tooltips on the rightmost bar / line point have breathing
                room and don't bleed against the modal's right edge.

                Round 96b (Feb 2026) — When either slot is a non-dividend
                stock, the Dividend Growth + Yield History charts are
                useless (one or both legs will be flat-zero). Swap them
                out for Revenue + EPS comparison charts which work for
                BOTH dividend and non-div tickers and surface real
                growth signal. The pair is "all non-div" → "all charts
                swap"; if even one side is non-div, swap so visual
                noise of a zero-bar doesn't dominate. */}
            {(() => {
              const aNonDiv = a?.detail?.pays_dividend === false;
              const bNonDiv = b?.detail?.pays_dividend === false;
              const eitherNonDiv = aNonDiv || bNonDiv;
              if (eitherNonDiv) {
                return (
                  <>
                    {/* Round 96b (Feb 2026) — Price Performance is the
                        universal fallback. Always renders for non-div
                        pairs because it works for ETFs/REITs/BDCs/CEFs
                        (which return empty Revenue+EPS from /triangle)
                        as well as for plain common stocks. */}
                    <div className="px-2 sm:px-6">
                      <MergedPerfChart
                        a={a}
                        b={b}
                        aTicker={a?.ticker}
                        bTicker={b?.ticker}
                      />
                    </div>
                    {(a?.triangle || b?.triangle) && (
                      <div className="px-2 sm:px-6">
                        <MergedFinancialChart
                          a={a?.triangle}
                          b={b?.triangle}
                          aTicker={a?.ticker}
                          bTicker={b?.ticker}
                          metric="revenue"
                        />
                      </div>
                    )}
                    {(() => {
                      // Round 99a (Jul 2026) — swap EPS chart for alt-metric
                      // pair when either slot is REIT/BDC/MLP/CEF. Each slot
                      // renders its own mini card since the two tickers may
                      // report different metrics (e.g. REIT FFO vs BDC NII)
                      // and can't share an axis.
                      const aAlt = a?.altMetrics?.asset_class || null;
                      const bAlt = b?.altMetrics?.asset_class || null;
                      if (aAlt || bAlt) {
                        return (
                          <div className="px-2 sm:px-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {aAlt ? (
                              <SingleMetricBarChart ticker={a?.ticker} altMetrics={a.altMetrics} />
                            ) : (
                              a?.triangle && (
                                <MergedFinancialChart
                                  a={a?.triangle}
                                  b={null}
                                  aTicker={a?.ticker}
                                  bTicker={null}
                                  metric="eps"
                                />
                              )
                            )}
                            {bAlt ? (
                              <SingleMetricBarChart ticker={b?.ticker} altMetrics={b.altMetrics} />
                            ) : (
                              b?.triangle && (
                                <MergedFinancialChart
                                  a={b?.triangle}
                                  b={null}
                                  aTicker={b?.ticker}
                                  bTicker={null}
                                  metric="eps"
                                />
                              )
                            )}
                          </div>
                        );
                      }
                      // Common-only pair — keep the shared-axis EPS chart.
                      return (a?.triangle || b?.triangle) && (
                        <div className="px-2 sm:px-6">
                          <MergedFinancialChart
                            a={a?.triangle}
                            b={b?.triangle}
                            aTicker={a?.ticker}
                            bTicker={b?.ticker}
                            metric="eps"
                          />
                        </div>
                      );
                    })()}
                  </>
                );
              }
              return (
                <>
                  {(a?.growth || b?.growth) && (
                    <div className="px-2 sm:px-6">
                      <MergedBarChart a={a?.growth} b={b?.growth} aTicker={a?.ticker} bTicker={b?.ticker} />
                    </div>
                  )}
                  {(a?.yieldHist || b?.yieldHist) && (
                    <div className="px-2 sm:px-6">
                      <MergedYieldChart
                        a={a?.yieldHist}
                        b={b?.yieldHist}
                        aTicker={a?.ticker}
                        bTicker={b?.ticker}
                        isDark={isDark}
                      />
                    </div>
                  )}
                  {/* Round 99a (Jul 2026) — Alt-metrics chart pair for
                      dividend-paying REIT/BDC/MLP/CEF pairs. The dividend
                      growth + yield charts above stay useful, but we
                      still want to surface FFO/NII/DCF because that's
                      how these asset classes actually earn. Renders when
                      either slot has a non-null asset_class. */}
                  {(() => {
                    const aAlt = a?.altMetrics?.asset_class || null;
                    const bAlt = b?.altMetrics?.asset_class || null;
                    if (!aAlt && !bAlt) return null;
                    return (
                      <div className="px-2 sm:px-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {aAlt && (
                          <SingleMetricBarChart ticker={a?.ticker} altMetrics={a.altMetrics} />
                        )}
                        {bAlt && (
                          <SingleMetricBarChart ticker={b?.ticker} altMetrics={b.altMetrics} />
                        )}
                      </div>
                    );
                  })()}
                </>
              );
            })()}
          </div>

          {/* Empty state — helper text + recently compared pairs */}
          {!a && !b && (
            <div
              className="rounded-lg border border-dashed border-border p-5 sm:p-6 space-y-4"
              data-testid="compare-empty-state"
            >
              <p className="text-center text-sm text-muted-foreground">
                Search and pick a ticker on each side to see a full comparison. Works with dividend payers, non-dividend growth stocks, ETFs, REITs, BDCs, CEFs, and MLPs.
              </p>
              {user && history.length > 0 && (
                <div className="space-y-2" data-testid="compare-recent-list">
                  <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                    <History className="w-3 h-3" />
                    Recently Compared
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {history.map((p, i) => (
                      <button
                        key={`${p.a}-${p.b || 'solo'}-${i}`}
                        type="button"
                        onClick={() => {
                          lockSlot('A', p.a);
                          if (p.b) lockSlot('B', p.b);
                        }}
                        className="inline-flex items-center gap-1 rounded-full border bg-muted/40 hover:bg-muted px-2.5 py-1 text-xs font-mono font-semibold transition-colors"
                        data-testid={`compare-recent-${p.a}${p.b ? '-' + p.b : ''}`}
                        title={p.b ? `Re-open ${p.a} vs ${p.b}` : `Re-open with ${p.a} in slot A`}
                      >
                        <span className={SLOT_STYLES.A.textLocked}>{p.a}</span>
                        {p.b ? (
                          <>
                            <span className="text-muted-foreground">vs</span>
                            <span className={SLOT_STYLES.B.textLocked}>{p.b}</span>
                          </>
                        ) : (
                          <span className="text-muted-foreground">· solo</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {(aDetail || bDetail) && (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2 pt-1">
              <button
                type="button"
                onClick={handleExportPng}
                disabled={exporting}
                className="inline-flex items-center gap-2 rounded-lg border bg-card hover:bg-muted px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="compare-export-png-btn"
                title="Download this comparison as an image you can share"
              >
                {exporting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 text-fuchsia-500 dark:text-fuchsia-400" />
                )}
                {exporting ? 'Rendering…' : 'Export as PNG'}
              </button>
              {slotA?.ticker && slotB?.ticker && (
                <button
                  type="button"
                  onClick={handleShareToX}
                  className="inline-flex items-center gap-2 rounded-lg border bg-card hover:bg-muted px-4 py-2 text-sm font-semibold transition-colors"
                  data-testid="compare-share-x-btn"
                  title="Compose a pre-filled post on X (Twitter). Tip: export the PNG first, then drag-drop it into the post."
                >
                  <Share2 className="w-4 h-4 text-sky-500 dark:text-sky-400" />
                  Share on X
                </button>
              )}
              {slotA?.ticker && slotB?.ticker && (
                <button
                  type="button"
                  onClick={handleTogglePin}
                  disabled={togglingPin}
                  className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    isCurrentPinned
                      ? 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300 hover:bg-amber-500/20'
                      : 'bg-card hover:bg-muted'
                  }`}
                  data-testid="compare-pin-pair-btn"
                  aria-pressed={isCurrentPinned}
                  title={
                    isCurrentPinned
                      ? `${slotA.ticker} vs ${slotB.ticker} is pinned — click to unpin`
                      : `Pin ${slotA.ticker} vs ${slotB.ticker} so it appears on your Watchlist tab as a one-click face-off card`
                  }
                >
                  {togglingPin ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : isCurrentPinned ? (
                    <PinOff className="w-4 h-4" />
                  ) : (
                    <Pin className="w-4 h-4 text-amber-500 dark:text-amber-400" />
                  )}
                  {isCurrentPinned ? 'Pinned' : 'Pin pair'}
                </button>
              )}
              {slotA?.ticker && slotB?.ticker && (
                <button
                  type="button"
                  onClick={handleCopyCaption}
                  className="inline-flex items-center gap-2 rounded-lg border bg-card hover:bg-muted px-4 py-2 text-sm font-semibold transition-colors"
                  data-testid="compare-copy-caption-btn"
                  title="Copy the takeaway caption to paste into Reddit, Discord, iMessage, anywhere"
                >
                  <ClipboardCopy className="w-4 h-4 text-emerald-500 dark:text-emerald-400" />
                  Copy
                </button>
              )}
            </div>
          )}

          {/* Round 89f — Pin Note editor row. Only renders when the
              pair is pinned. Display mode shows the saved memo (or a
              soft "Add a note" affordance); edit mode swaps to an
              inline textarea with Save/Cancel. Notes are capped 140
              chars (matching server-side enforcement). */}
          {isCurrentPinned && slotA?.ticker && slotB?.ticker && (
            <div className="flex items-start justify-center gap-2 -mt-1" data-testid="compare-pin-note-row">
              {!noteOpen ? (
                <button
                  type="button"
                  onClick={() => {
                    setNoteDraft(currentPinNote || '');
                    setNoteOpen(true);
                  }}
                  className="inline-flex items-center gap-1.5 max-w-full text-[11px] px-2.5 py-1 rounded-md border border-amber-500/30 bg-amber-500/5 hover:bg-amber-500/10 text-amber-700 dark:text-amber-300 transition-colors"
                  data-testid="compare-pin-note-open"
                  title="Add a 1-line memo so you remember WHY this face-off matters when you come back later"
                >
                  <StickyNote className="w-3 h-3 shrink-0" />
                  {currentPinNote ? (
                    <span className="italic truncate max-w-[420px]" title={currentPinNote}>
                      &ldquo;{currentPinNote}&rdquo;
                    </span>
                  ) : (
                    <span className="opacity-80">Add a note…</span>
                  )}
                </button>
              ) : (
                <div className="flex items-stretch gap-1.5 w-full max-w-md" data-testid="compare-pin-note-editor">
                  <input
                    type="text"
                    autoFocus
                    value={noteDraft}
                    onChange={(e) => setNoteDraft(e.target.value.slice(0, 140))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSaveNote();
                      } else if (e.key === 'Escape') {
                        setNoteOpen(false);
                      }
                    }}
                    maxLength={140}
                    placeholder={`e.g. "watching post-earnings drop", "roth target"`}
                    className="flex-1 min-w-0 px-2.5 py-1 rounded-md border border-amber-500/40 bg-background text-xs focus:outline-none focus:ring-2 focus:ring-amber-500/40"
                    data-testid="compare-pin-note-input"
                  />
                  <span className={`text-[10px] font-mono self-center px-1 ${noteDraft.length >= 130 ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground/60'}`}>
                    {noteDraft.length}/140
                  </span>
                  <button
                    type="button"
                    onClick={handleSaveNote}
                    disabled={savingNote}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-bold bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-50"
                    data-testid="compare-pin-note-save"
                  >
                    {savingNote ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => setNoteOpen(false)}
                    disabled={savingNote}
                    className="inline-flex items-center px-2 py-1 rounded-md text-xs font-semibold border border-border hover:bg-muted disabled:opacity-50"
                    data-testid="compare-pin-note-cancel"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          )}

          {(aDetail || bDetail) && (
            <p className="text-center text-[10px] text-muted-foreground">
              Payout ratio is estimated. Historical yield is approximated from price and dividend rate. Not financial advice.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
